import React, {
  useEffect,
  useMemo,
  useState
} from "react";

import ExcelJS from "exceljs";

import {
  CalendarDays,
  CheckCircle2,
  ChevronRight,
  ClipboardList,
  GraduationCap,
  Lock,
  School,
  Target,
  User,
  Users,
  X
} from "lucide-react";

import {
  getActivePlanningConfig
} from "../../data/planning/planningConfig";

import {
  getGroupedTargets,
  getTargetById
} from "../../data/planning/targetCatalog";

import {
  getPlanningRuleProfile
} from "../../data/planning/planningRules";

import {
  getTargetActivityById,
  getTargetActivityOptions
} from "../../data/planning/targetActivityCatalog";


import {
  ADDITIONAL_GROUP_ACTIVITY_CATALOG,
  getAdditionalGroupActivityById,
  getAdditionalGroupActivityOptions
} from "../../data/planning/additionalGroupActivityCatalog";

import {
  getMebbisDisplayCode
} from "../../data/planning/mebbisCodeMap";

import {
  getExactMebbisCode
} from "../../data/planning/mebbisExactCodeMap";

import {
  buildSelectedActivityRows,
  buildAutoTargetActivityRows
} from "../../utils/planning/planRowBuilder";

import {
  generateAnnualPlanDraft,
  generateCorePlanRows
} from "../../utils/planning/annualPlanEngine";

import {
  schedulePlanRows,
  SCHOOL_CALENDAR_2026_2027
} from "../../utils/planning/planScheduler";


import {
  resolveAutoTargetActivities
} from "../../utils/planning/autoTargetResolver";

import {
  buildAutoTargetActivitySlots
} from "../../utils/planning/autoPlanSlotBuilder";

import {
  getTargetActivityTypeOptions,
  getDefaultActivityTypeForAdditionalCategory,
  getActivityTypeById
} from "../../data/planning/activityTypeCatalog";

const SCHOOL_LEVELS = [
  {
    value: "preschool",
    label: "Okul Öncesi"
  },
  {
    value: "primary",
    label: "İlkokul"
  },
  {
    value: "middle_school",
    label: "Ortaokul"
  },
  {
    value: "high_school",
    label: "Lise"
  },
  {
    value: "vocational_high_school",
    label: "Mesleki ve Teknik Anadolu Lisesi"
  }
];


const createEmptyActivitySelections = () => ({
  general: {
    student: [],
    parent: [],
    teacher: []
  },

  local: {
    student: [],
    parent: [],
    teacher: []
  },

  special1: {
    student: [],
    parent: [],
    teacher: []
  },

  special2: {
    student: [],
    parent: [],
    teacher: []
  }
});


const DEVELOPMENT_AREA_LABELS = {
  social_emotional: "Sosyal Duygusal",
  academic: "Akademik",
  career: "Kariyer"
};


// =========================================================
// HEDEF GÖRÜNÜR ETİKETİ
// =========================================================
//
// Resmî hedef kataloğunu değiştirmiyoruz.
// "Akran Zorbalığı" sistem içinde resmî hedef adı olarak
// kalırken kullanıcıya ve plan çıktısına daha açıklayıcı
// biçimde gösterilir.
// =========================================================

const getPlanningTargetDisplayLabel =
  label => {

    const safeLabel =
      String(label || "").trim();

    if (
      safeLabel ===
      "Akran Zorbalığı"
    ) {
      return (
        "Akran Zorbalığı / Akran Nezaketi"
      );
    }

    return safeLabel;
  };

const QUICK_CLASS_BRANCHES = [
  "A",
  "B",
  "C",
  "D",
  "E",
  "F",
  "G",
  "H"
];


const QUICK_GRADE_OPTIONS_BY_SCHOOL_LEVEL = {

  preschool: [
    {
      key: "preschool",
      grade: null,
      label: "Okul Öncesi"
    }
  ],

  primary: [
    { key: "grade_1", grade: 1, label: "1. Sınıf" },
    { key: "grade_2", grade: 2, label: "2. Sınıf" },
    { key: "grade_3", grade: 3, label: "3. Sınıf" },
    { key: "grade_4", grade: 4, label: "4. Sınıf" }
  ],

  middle_school: [
    { key: "grade_5", grade: 5, label: "5. Sınıf" },
    { key: "grade_6", grade: 6, label: "6. Sınıf" },
    { key: "grade_7", grade: 7, label: "7. Sınıf" },
    { key: "grade_8", grade: 8, label: "8. Sınıf" }
  ],

  high_school: [
    { key: "grade_9", grade: 9, label: "9. Sınıf" },
    { key: "grade_10", grade: 10, label: "10. Sınıf" },
    { key: "grade_11", grade: 11, label: "11. Sınıf" },
    { key: "grade_12", grade: 12, label: "12. Sınıf" }
  ],

  vocational_high_school: [
    { key: "grade_9", grade: 9, label: "9. Sınıf" },
    { key: "grade_10", grade: 10, label: "10. Sınıf" },
    { key: "grade_11", grade: 11, label: "11. Sınıf" },
    { key: "grade_12", grade: 12, label: "12. Sınıf" }
  ]

};

const PlanningCenter = ({
  academicYear = "2026-2027",
  user = null,
  managedClasses = [],
  onOpenClassManager = null
}) => {

  const config =
    getActivePlanningConfig(
      academicYear
    );


  const groupedTargets =
    useMemo(
      () => getGroupedTargets(),
      []
    );


  const [schoolLevel, setSchoolLevel] =
    useState("");

  const [localTargetId, setLocalTargetId] =
    useState("");

  const [specialTarget1Id, setSpecialTarget1Id] =
    useState("");

  const [specialTarget2Id, setSpecialTarget2Id] =
    useState("");

  const [showSummary, setShowSummary] =
    useState(false);

const [selectedActivities, setSelectedActivities] =
  useState(
    createEmptyActivitySelections()
  );

const [activeTargetType, setActiveTargetType] =
  useState("general");

const [activitiesConfirmed, setActivitiesConfirmed] =
  useState(false);


const [
  selectedAdditionalActivities,
  setSelectedAdditionalActivities
] = useState([]);

const [
  additionalCategory,
  setAdditionalCategory
] = useState("");

const [
  additionalSearch,
  setAdditionalSearch
] = useState("");

const [
  preparedActivityRows,
  setPreparedActivityRows
] = useState([]);

const [
  generatedAnnualPlan,
  setGeneratedAnnualPlan
] = useState(null);

const [
  planGenerationErrors,
  setPlanGenerationErrors
] = useState([]);

const [
  selectedActivityTypes,
  setSelectedActivityTypes
] = useState({});

const [
  selectedActivityLevels,
  setSelectedActivityLevels
] = useState({});

const [
  selectedAdditionalActivityTypes,
  setSelectedAdditionalActivityTypes
] = useState({});

const [
  activeStep,
  setActiveStep
] = useState(1);

const [
  showStandardActivities,
  setShowStandardActivities
] = useState(false);

const [
  excludedStandardCoreIds,
  setExcludedStandardCoreIds
] = useState([]);

const [
  standardActivityTypeOverrides,
  setStandardActivityTypeOverrides
] = useState({});


/*
 * Plan çıktısında kullanılacak sınıf/şube yapısı.
 *
 * Şimdilik manuel tanımlanabilir.
 * Daha sonra ClassManager'daki aktif yıl sınıfları
 * aynı veri modeline bağlanacaktır.
 */
const [
  classStructureText,
  setClassStructureText
] = useState("");


const [
  planClassOptions,
  setPlanClassOptions
] = useState([]);


const [
  classSetupMode,
  setClassSetupMode
] = useState("");


const [
  quickClassSelections,
  setQuickClassSelections
] = useState({});


const [
  editingCoverageRowId,
  setEditingCoverageRowId
] = useState(null);

const [
  planningDraftHydrated,
  setPlanningDraftHydrated
] = useState(false);

const [
  coverageOverrides,
  setCoverageOverrides
] = useState({});

const [
  dateOverrides,
  setDateOverrides
] = useState({});

const [
  editingDateRowId,
  setEditingDateRowId
] = useState(null);

const [
  dateEditDraft,
  setDateEditDraft
] = useState({
  startDate: "",
  endDate: ""
});

const [
  dateEditError,
  setDateEditError
] = useState("");


const [
  autoTargetResolutionReport,
  setAutoTargetResolutionReport
] = useState(null);


const [
  autoCatalogAuditReport,
  setAutoCatalogAuditReport
] = useState(null);


const [
  planningMode,
  setPlanningMode
] = useState("auto");


const managedClassLabels =
  useMemo(
    () => {

      const safeClasses =
        Array.isArray(
          managedClasses
        )
          ? managedClasses
          : [];


      return Array.from(
        new Set(
          safeClasses

           /*
 * Planlama Merkezi yalnız seçili
 * eğitim öğretim yılına ait sınıfları kullanır.
 *
 * academicYear alanı olmayan legacy kayıtlar
 * burada bilinçli olarak dışarıda bırakılır.
 */
.filter(
  item =>
    item?.academicYear ===
      academicYear
)

            .map(
              item =>
                String(
                  item?.name ||
                  item?.label ||
                  ""
                )
                  .trim()
                  .toLocaleUpperCase(
                    "tr-TR"
                  )
            )

            .filter(Boolean)
        )
      ).sort(
        (a, b) =>
          a.localeCompare(
            b,
            "tr-TR",
            {
              numeric: true
            }
          )
      );
    },
    [
      managedClasses,
      academicYear
    ]
  );





const planningUserId =
  String(
    user?.uid || ""
  ).trim();


const planningDraftStorageKey =
  planningUserId
    ? `rehber360_planning_draft_v2_${planningUserId}_${academicYear}`
    : null;

useEffect(() => {

  /*
   * Audit okul kademesine bağlıdır.
   * Kademe değişirse eski sonuç geçersizdir.
   */
  setAutoCatalogAuditReport(
    null
  );

}, [
  schoolLevel
]);


useEffect(() => {

  /*
   * Kademe değiştiğinde eski hızlı
   * sınıf seçimleri yeni kademeye taşınmaz.
   */
  setQuickClassSelections({});

}, [
  schoolLevel
]);

// =========================================================
// PLANLAMA TASLAĞINI GERİ YÜKLE
// =========================================================

useEffect(() => {

  try {

    if (
      !planningDraftStorageKey ||
      !planningUserId
    ) {

      setPlanningDraftHydrated(
        true
      );

      return;
    }


    const rawDraft =
      window.localStorage.getItem(
        planningDraftStorageKey
      );


    if (!rawDraft) {

      setPlanningDraftHydrated(
        true
      );

      return;
    }


    const draft =
      JSON.parse(
        rawDraft
      );


   if (
  draft?.version !== 2 ||
  draft?.ownerUid !== planningUserId
) {

      setPlanningDraftHydrated(
        true
      );

      return;
    }


    if (draft.schoolLevel) {
      setSchoolLevel(
        draft.schoolLevel
      );
    }


    setLocalTargetId(
      draft.localTargetId ||
      ""
    );

    setSpecialTarget1Id(
      draft.specialTarget1Id ||
      ""
    );

    setSpecialTarget2Id(
      draft.specialTarget2Id ||
      ""
    );


    if (
      draft.selectedActivities &&
      typeof draft.selectedActivities ===
        "object"
    ) {

      setSelectedActivities(
        draft.selectedActivities
      );
    }


    if (
      Array.isArray(
        draft.selectedAdditionalActivities
      )
    ) {

      setSelectedAdditionalActivities(
        draft.selectedAdditionalActivities
      );
    }


    if (
      draft.selectedActivityLevels &&
      typeof draft.selectedActivityLevels ===
        "object"
    ) {

      setSelectedActivityLevels(
        draft.selectedActivityLevels
      );
    }


    if (
      draft.selectedActivityTypes &&
      typeof draft.selectedActivityTypes ===
        "object"
    ) {

      setSelectedActivityTypes(
        draft.selectedActivityTypes
      );
    }


       if (
      Array.isArray(
        draft.excludedStandardCoreIds
      )
    ) {

      setExcludedStandardCoreIds(
        draft.excludedStandardCoreIds
      );
    }


    if (
      draft.standardActivityTypeOverrides &&
      typeof draft.standardActivityTypeOverrides ===
        "object"
    ) {

      setStandardActivityTypeOverrides(
        draft.standardActivityTypeOverrides
      );
    }


    setClassStructureText(
      draft.classStructureText ||
      ""
    );


    if (
      Array.isArray(
        draft.planClassOptions
      )
    ) {

      setPlanClassOptions(
        draft.planClassOptions
      );
    }

if (
  draft.coverageOverrides &&
  typeof draft.coverageOverrides ===
    "object"
) {

  setCoverageOverrides(
    draft.coverageOverrides
  );
}


if (
  draft.dateOverrides &&
  typeof draft.dateOverrides ===
    "object"
) {

  setDateOverrides(
    draft.dateOverrides
  );
}


        setActiveTargetType(
      draft.activeTargetType ||
      "general"
    );


    if (
      draft.planningMode === "auto" ||
      draft.planningMode === "manual"
    ) {

      setPlanningMode(
        draft.planningMode
      );
    }


    /*
     * Hedefler daha önce tamamlandıysa
     * doğrudan faaliyet ekranına dönebilir.
     */
    const hasTargetStructure =
      Boolean(
        draft.schoolLevel &&
        draft.localTargetId &&
        draft.specialTarget1Id &&
        draft.specialTarget2Id
      );


    setShowSummary(
      hasTargetStructure
    );


    /*
     * Eski üretilmiş planı restore etmiyoruz.
     * Motorun güncel kodla yeniden üretmesini istiyoruz.
     *
     * Kullanıcı en fazla Faaliyetleri Onayla'ya
     * yeniden basacak.
     */
    setActiveStep(
      hasTargetStructure
        ? 2
        : 1
    );

    setActivitiesConfirmed(
      false
    );

    setGeneratedAnnualPlan(
      null
    );

  } catch (error) {

    console.warn(
      "Planlama taslağı yüklenemedi:",
      error
    );

  } finally {

    setPlanningDraftHydrated(
      true
    );

  }

}, [
  planningDraftStorageKey,
  planningUserId
]);

// =========================================================
// PLANLAMA TASLAĞINI OTOMATİK KAYDET
// =========================================================

useEffect(() => {

  /*
   * İlk render sırasında boş state'in
   * eski taslağın üzerine yazılmasını engeller.
   */
 if (
  !planningDraftHydrated ||
  !planningDraftStorageKey ||
  !planningUserId
) {
  return;
}


  const draft = {

  version: 2,

  ownerUid:
    planningUserId,

  academicYear,

    schoolLevel,

    localTargetId,

    specialTarget1Id,

    specialTarget2Id,

    selectedActivities,

    selectedAdditionalActivities,

    selectedActivityLevels,

    selectedActivityTypes,

        excludedStandardCoreIds,

    standardActivityTypeOverrides,

    classStructureText,

planClassOptions,

coverageOverrides,

dateOverrides,

activeTargetType,

planningMode,

savedAt:
      new Date().toISOString()
  };


  try {

    window.localStorage.setItem(
      planningDraftStorageKey,
      JSON.stringify(
        draft
      )
    );

  } catch (error) {

    console.warn(
      "Planlama taslağı kaydedilemedi:",
      error
    );

  }

}, [
  planningDraftHydrated,
  planningDraftStorageKey,
  planningUserId,
  academicYear,
  schoolLevel,
  localTargetId,
  specialTarget1Id,
  specialTarget2Id,
  selectedActivities,
  selectedAdditionalActivities,
  selectedActivityLevels,
   selectedActivityTypes,
  excludedStandardCoreIds,
  standardActivityTypeOverrides,
  classStructureText,
planClassOptions,
coverageOverrides,
dateOverrides,
activeTargetType,
planningMode
]);

// =========================================================
// SATIR BAZLI SINIF SEÇİMLERİNİ TASLAKTA TUT
// =========================================================

useEffect(() => {

  if (
    !generatedAnnualPlan?.activities
  ) {
    return;
  }


  const nextOverrides = {};


  generatedAnnualPlan.activities
    .forEach(row => {

      /*
       * Tüm sınıflar zaten varsayılan davranış.
       * Yalnızca kullanıcının özel olarak
       * değiştirdiği satırları saklıyoruz.
       */
      if (
        row?.coverage?.mode !==
        "selected_classes"
      ) {
        return;
      }


      nextOverrides[row.id] = {

        mode:
          "selected_classes",

        classIds:
          Array.isArray(
            row.coverage.classIds
          )
            ? row.coverage.classIds
            : [],

        classLabels:
          Array.isArray(
            row.coverage.classLabels
          )
            ? row.coverage.classLabels
            : [],

        displayLabel:
          row.coverage.displayLabel ||
          "Sınıf seçilmedi"
      };
    });


  setCoverageOverrides(
    nextOverrides
  );

}, [
  generatedAnnualPlan
]);


if (!config) {

    return (
      <div className="max-w-5xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-3xl p-8">
          <h2 className="text-xl font-black text-red-800">
            Planlama yılı bulunamadı
          </h2>

          <p className="text-red-600 mt-2">
            {academicYear} için aktif planlama
            konfigürasyonu bulunmuyor.
          </p>
        </div>
      </div>
    );
  }

const ruleProfile =
  getPlanningRuleProfile(
    "regular_school"
  );


// =========================================================
// PLAN SINIF / ŞUBE YAPISI
// =========================================================

const createManualClassId =
  label => {

    const safeLabel =
      String(label || "")
        .trim()
        .toLocaleLowerCase("tr-TR")
        .replace(/ı/g, "i")
        .replace(/ğ/g, "g")
        .replace(/ü/g, "u")
        .replace(/ş/g, "s")
        .replace(/ö/g, "o")
        .replace(/ç/g, "c")
        .replace(
          /[^a-z0-9]+/g,
          "_"
        )
        .replace(
          /^_+|_+$/g,
          ""
        );

    return (
      `manual_class_${safeLabel}`
    );
  };



// =========================================================
// SINIF DÜZEYİ ÇÖZÜMLEME
// =========================================================
//
// Desteklenen örnekler:
//
// 8-A
// 8/A
// 8A
// 8 A
// 12-B
// 12/A
//
// Sınıf etiketi sayı ile başlamıyorsa null döner.
// =========================================================

const getGradeFromClassLabel =
  label => {

    const match =
      String(
        label || ""
      )
        .trim()
        .match(
          /^(\d{1,2})/
        );


    if (!match) {
      return null;
    }


    const grade =
      Number(
        match[1]
      );


    return (
      Number.isInteger(
        grade
      ) &&
      grade >= 1 &&
      grade <= 12
    )
      ? grade
      : null;
  };


const getClassGrade =
  classItem => {

    const storedGrade =
      Number(
        classItem?.grade
      );


    if (
      Number.isInteger(
        storedGrade
      ) &&
      storedGrade >= 1 &&
      storedGrade <= 12
    ) {
      return storedGrade;
    }


    return getGradeFromClassLabel(
      classItem?.label
    );
  };


const getApplicableClassOptionsForRow = (
  row,
  classOptions =
    planClassOptions
) => {

  const applicableGrades =
    Array.isArray(
      row?.applicableGrades
    )
      ? row.applicableGrades
          .map(
            grade =>
              Number(grade)
          )
          .filter(
            grade =>
              Number.isInteger(
                grade
              )
          )
      : [];


  /*
   * Sınıf düzeyi kısıtı yoksa
   * mevcut davranış aynen devam eder.
   */
  if (
    applicableGrades.length === 0
  ) {
    return classOptions;
  }


  return classOptions.filter(
    classItem =>
      applicableGrades.includes(
        getClassGrade(
          classItem
        )
      )
  );
};


const parseManualClassStructure =
  value => {

    const labels =
      String(value || "")
        .split(
          /[,;\n]+/
        )
        .map(
          item =>
            item.trim()
        )
        .filter(Boolean);


    const uniqueLabels =
      Array.from(
        new Set(labels)
      );


    return uniqueLabels
      .sort(
        (a, b) =>
          a.localeCompare(
            b,
            "tr-TR",
            {
              numeric: true
            }
          )
      )
            .map(label => ({
        id:
          createManualClassId(
            label
          ),

        label,

        grade:
          getGradeFromClassLabel(
            label
          ),

        source:
          "manual"
      }));
  };


const applyPlanClassCoverage = (
  rows = [],
  classOptions =
    planClassOptions
) => {

  const safeRows =
    Array.isArray(rows)
      ? rows
      : [];


  return safeRows.map(row => {

    /*
     * Satır belirli sınıf düzeyleriyle
     * sınırlandırılmışsa yalnız o düzeydeki
     * şubeler uygun kabul edilir.
     */
    const applicableClassOptions =
      getApplicableClassOptionsForRow(
        row,
        classOptions
      );


    const classIds =
      applicableClassOptions.map(
        item => item.id
      );


    const classLabels =
      applicableClassOptions.map(
        item => item.label
      );


    const classMap =
      new Map(
        applicableClassOptions.map(
          item => [
            item.id,
            item
          ]
        )
      );


    /*
     * TÜM UYGUN SINIFLAR
     *
     * applicableGrades yoksa gerçekten
     * okulun tüm sınıflarıdır.
     *
     * applicableGrades varsa yalnız
     * ilgili düzeyin tüm şubeleridir.
     */
    if (
      row?.coverage?.mode ===
      "all_classes"
    ) {

      return {
        ...row,

        coverage: {
          ...row.coverage,

          classIds,

          classLabels,

          displayLabel:
            classLabels.length > 0
              ? classLabels.join(", ")
              : Array.isArray(
                  row?.applicableGrades
                ) &&
                row.applicableGrades.length >
                  0
                ? "Uygun sınıf bulunamadı"
                : "Tüm Sınıflar",

          resolved:
            classLabels.length > 0
        }
      };
    }


    /*
     * MANUEL SEÇİLMİŞ SINIFLAR
     *
     * Eski override içinde artık bu satıra
     * uygun olmayan bir sınıf bulunuyorsa
     * otomatik olarak temizlenir.
     */
    if (
      row?.coverage?.mode ===
      "selected_classes"
    ) {

      const validClassIds =
        (
          row.coverage.classIds ||
          []
        ).filter(
          classId =>
            classMap.has(
              classId
            )
        );


      const validClassLabels =
        validClassIds.map(
          classId =>
            classMap.get(
              classId
            )?.label
        ).filter(Boolean);


      return {
        ...row,

        coverage: {
          ...row.coverage,

          classIds:
            validClassIds,

          classLabels:
            validClassLabels,

          displayLabel:
            validClassLabels.length > 0
              ? validClassLabels.join(", ")
              : "Sınıf seçilmedi",

          resolved:
            validClassLabels.length > 0
        }
      };
    }


    return row;
  });
};

const applyCoverageOverrides = (
  rows = []
) => {

  const safeRows =
    Array.isArray(rows)
      ? rows
      : [];


  return safeRows.map(row => {

    const override =
      coverageOverrides[
        row.id
      ];


    if (
      !override ||
      override.mode !==
        "selected_classes"
    ) {
      return row;
    }


    /*
     * Override yalnız bu satır için
     * gerçekten uygun olan sınıflar
     * üzerinden yeniden doğrulanır.
     */
    const applicableClassOptions =
      getApplicableClassOptionsForRow(
        row,
        planClassOptions
      );


    const classMap =
      new Map(
        applicableClassOptions.map(
          item => [
            item.id,
            item
          ]
        )
      );


    const validClassIds =
      (
        override.classIds ||
        []
      ).filter(
        classId =>
          classMap.has(
            classId
          )
      );


    const validClassLabels =
      validClassIds.map(
        classId =>
          classMap.get(
            classId
          )?.label
      ).filter(Boolean);


    return {
      ...row,

      coverage: {
        ...row.coverage,

        mode:
          "selected_classes",

        classIds:
          validClassIds,

        classLabels:
          validClassLabels,

        displayLabel:
          validClassLabels.length > 0
            ? validClassLabels.join(", ")
            : "Sınıf seçilmedi",

        resolved:
          validClassLabels.length > 0
      }
    };
  });
};

// =========================================================
// SATIR BAZLI TARİH DÜZENLEMELERİ
// =========================================================
const getDateOverrideKey =
  rowId =>
    `${schoolLevel}__${rowId}`;


const applyDateOverrides = (
  rows = []
) => {

  const safeRows =
    Array.isArray(rows)
      ? rows
      : [];


  return safeRows.map(row => {

    const override =
      dateOverrides[
        getDateOverrideKey(
          row.id
        )
      ];


    if (
      !override?.startDate ||
      !override?.endDate
    ) {
      return row;
    }


    return {
      ...row,

      startDate:
        override.startDate,

      endDate:
        override.endDate,

      month:
        Number(
          override.startDate
            .slice(5, 7)
        ),

      scheduledWeek:
        null,

      dateSource:
        "manual_override"
    };
  });
};


const handleStartDateEdit =
  row => {

    if (!row?.id) {
      return;
    }


    setEditingDateRowId(
      row.id
    );

    setDateEditDraft({
      startDate:
        row.startDate || "",
      endDate:
        row.endDate || ""
    });

    setDateEditError("");
  };


const handleCancelDateEdit =
  () => {

    setEditingDateRowId(null);

    setDateEditDraft({
      startDate: "",
      endDate: ""
    });

    setDateEditError("");
  };


const handleSaveDateEdit =
  row => {

    if (!row?.id) {
      return;
    }


    const startDate =
      dateEditDraft.startDate;

    const endDate =
      dateEditDraft.endDate;


    if (
      !startDate ||
      !endDate
    ) {

      setDateEditError(
        "Başlangıç ve bitiş tarihini birlikte seçin."
      );

      return;
    }


    if (
      endDate < startDate
    ) {

      setDateEditError(
        "Bitiş tarihi başlangıç tarihinden önce olamaz."
      );

      return;
    }


    const schoolYearStart =
      SCHOOL_CALENDAR_2026_2027
        .firstTerm.start;

    const schoolYearEnd =
      SCHOOL_CALENDAR_2026_2027
        .secondTerm.end;


    if (
      startDate < schoolYearStart ||
      startDate > schoolYearEnd ||
      endDate < schoolYearStart ||
      endDate > schoolYearEnd
    ) {

      setDateEditError(
        `Tarih ${schoolYearStart} ile ${schoolYearEnd} arasında olmalıdır.`
      );

      return;
    }


    const overrideKey =
      getDateOverrideKey(
        row.id
      );


    setDateOverrides(
      prev => ({
        ...prev,

        [overrideKey]: {
          startDate,
          endDate
        }
      })
    );


    setGeneratedAnnualPlan(
      previousPlan => {

        if (!previousPlan) {
          return previousPlan;
        }


        return {
          ...previousPlan,

          activities:
            (
              previousPlan.activities ||
              []
            ).map(activityRow => {

              if (
                activityRow.id !==
                row.id
              ) {
                return activityRow;
              }


              return {
                ...activityRow,

                startDate,

                endDate,

                month:
                  Number(
                    startDate.slice(
                      5,
                      7
                    )
                  ),

                scheduledWeek:
                  null,

                dateSource:
                  "manual_override"
              };
            })
        };
      }
    );


    handleCancelDateEdit();
  };




// =========================================================
// SINIF / ŞUBE KAYNAKLARI
// =========================================================

const applyClassOptionsToPlan =
  nextClasses => {

    const safeClasses =
      Array.isArray(
        nextClasses
      )
        ? nextClasses
        : [];


    setPlanClassOptions(
      safeClasses
    );


    /*
     * Gelişmiş metin alanını da
     * güncel sınıf yapısıyla senkron tut.
     */
    setClassStructureText(
      safeClasses
        .map(
          item => item.label
        )
        .join(", ")
    );


    /*
     * Plan zaten oluşturulmuşsa
     * sınıf kapsamlarını anında güncelle.
     */
    setGeneratedAnnualPlan(
      previousPlan => {

        if (!previousPlan) {
          return previousPlan;
        }


        return {
          ...previousPlan,

          activities:
            applyPlanClassCoverage(
              previousPlan.activities,
              safeClasses
            )
        };
      }
    );
  };


const handleApplyClassStructure =
  () => {

    const nextClasses =
      parseManualClassStructure(
        classStructureText
      );


    applyClassOptionsToPlan(
      nextClasses
    );
  };


const handleUseManagedClasses =
  () => {

    if (
      managedClassLabels.length === 0
    ) {
      return;
    }


    const nextClasses =
      parseManualClassStructure(
        managedClassLabels.join(
          ", "
        )
      ).map(
        item => ({
          ...item,
          source:
            "class_manager"
        })
      );


    applyClassOptionsToPlan(
      nextClasses
    );
  };


const handleToggleQuickBranch = (
  gradeKey,
  branch
) => {

  setQuickClassSelections(
    previous => {

      const current =
        Array.isArray(
          previous[gradeKey]
        )
          ? previous[gradeKey]
          : [];


      const next =
        current.includes(
          branch
        )
          ? current.filter(
              item =>
                item !== branch
            )
          : [
              ...current,
              branch
            ];


      return {
        ...previous,
        [gradeKey]:
          next
      };
    }
  );
};


const quickGradeOptions =
  QUICK_GRADE_OPTIONS_BY_SCHOOL_LEVEL[
    schoolLevel
  ] || [];


const quickClassLabels =
  quickGradeOptions.flatMap(
    gradeOption => {

      const branches =
        quickClassSelections[
          gradeOption.key
        ] || [];


      return branches.map(
        branch =>
          gradeOption.grade
            ? `${gradeOption.grade}/${branch}`
            : branch
      );
    }
  );


const handleApplyQuickClasses =
  () => {

    if (
      quickClassLabels.length === 0
    ) {
      return;
    }


    const nextClasses =
      parseManualClassStructure(
        quickClassLabels.join(
          ", "
        )
      ).map(
        item => ({
          ...item,
          source:
            "quick_builder"
        })
      );


    applyClassOptionsToPlan(
      nextClasses
    );
  };



// =========================================================
// SATIR BAZLI SINIF / ŞUBE KAPSAMI
// =========================================================

const getRowSelectedClassIds =
  row => {

    if (
      row?.coverage?.mode ===
      "all_classes"
    ) {

      return getApplicableClassOptionsForRow(
        row,
        planClassOptions
      ).map(
        item => item.id
      );
    }


    return Array.isArray(
      row?.coverage?.classIds
    )
      ? row.coverage.classIds
      : [];
  };

const handleSetRowAllClasses =
  rowId => {

    setGeneratedAnnualPlan(
      previousPlan => {

        if (!previousPlan) {
          return previousPlan;
        }


        return {
          ...previousPlan,

          activities:
            (
              previousPlan.activities ||
              []
            ).map(row => {

              if (
                row.id !== rowId
              ) {
                return row;
              }


              const applicableClassOptions =
                getApplicableClassOptionsForRow(
                  row,
                  planClassOptions
                );


              const classIds =
                applicableClassOptions.map(
                  item => item.id
                );


              const classLabels =
                applicableClassOptions.map(
                  item => item.label
                );


              return {
                ...row,

                coverage: {
                  ...row.coverage,

                  mode:
                    "all_classes",

                  classIds,

                  classLabels,

                  displayLabel:
                    classLabels.length > 0
                      ? classLabels.join(", ")
                      : "Uygun sınıf bulunamadı",

                  resolved:
                    classLabels.length > 0
                }
              };
            })
        };
      }
    );
  };

const handleToggleRowClass = (
  rowId,
  classItem
) => {

  setGeneratedAnnualPlan(
    previousPlan => {

      if (!previousPlan) {
        return previousPlan;
      }


      return {
        ...previousPlan,

        activities:
          (
            previousPlan.activities ||
            []
          ).map(row => {

            if (
              row.id !== rowId
            ) {
              return row;
            }


            const applicableClassOptions =
              getApplicableClassOptionsForRow(
                row,
                planClassOptions
              );


            const isApplicableClass =
              applicableClassOptions.some(
                item =>
                  item.id ===
                  classItem.id
              );


            /*
             * Grade kısıtının dışında kalan
             * bir sınıf programa eklenemez.
             */
            if (!isApplicableClass) {
              return row;
            }


            const currentIds =
              getRowSelectedClassIds(
                row
              );


            const nextIds =
              currentIds.includes(
                classItem.id
              )
                ? currentIds.filter(
                    id =>
                      id !==
                      classItem.id
                  )
                : [
                    ...currentIds,
                    classItem.id
                  ];


            const nextClasses =
              applicableClassOptions.filter(
                item =>
                  nextIds.includes(
                    item.id
                  )
              );


            const nextLabels =
              nextClasses.map(
                item => item.label
              );


            const allSelected =
              applicableClassOptions.length >
                0 &&
              nextIds.length ===
                applicableClassOptions.length;


            return {
              ...row,

              coverage: {
                ...row.coverage,

                mode:
                  allSelected
                    ? "all_classes"
                    : "selected_classes",

                classIds:
                  nextIds,

                classLabels:
                  nextLabels,

                displayLabel:
                  nextLabels.length > 0
                    ? nextLabels.join(", ")
                    : "Sınıf seçilmedi",

                resolved:
                  nextLabels.length > 0
              }
            };
          })
      };
    }
  );
};


const selectedSchoolLevelLabel =
  SCHOOL_LEVELS.find(
    level =>
      level.value === schoolLevel
  )?.label || "";


const standardPlanResult =
  schoolLevel
    ? generateCorePlanRows({
        academicYear,
        profileId:
          "regular_school",
        schoolLevel,
        activityTypeOverrides:
          standardActivityTypeOverrides
      })
    : {
        rows: [],
        errors: [],
        warnings: []
      };


const standardPlanRows =
  Array.isArray(
    standardPlanResult?.rows
  )
    ? standardPlanResult.rows
    : [];




const standardPlanIncludedCount =
  standardPlanRows.filter(
    row =>
      row.locked ||
      !excludedStandardCoreIds.includes(
        row.coreId
      )
  ).length;


const getStandardAudienceLabel =
  audience => {

    if (audience === "student") {
      return "Öğrenci";
    }

    if (audience === "parent") {
      return "Veli";
    }

    if (audience === "teacher") {
      return "Öğretmen";
    }

    return null;
  };


const handleToggleStandardActivity =
  row => {

    if (
      !row?.coreId ||
      row.locked
    ) {
      return;
    }

    setExcludedStandardCoreIds(
      prev =>
        prev.includes(
          row.coreId
        )
          ? prev.filter(
              id =>
                id !==
                row.coreId
            )
          : [
              ...prev,
              row.coreId
            ]
    );

    /*
     * Standart plan yapısı değişti.
     * Daha önce üretilmiş yıllık taslak
     * artık güncel değildir.
     */
    setGeneratedAnnualPlan(null);

    setPlanGenerationErrors([]);

    setActivitiesConfirmed(false);
  };


const getStandardActivityTypeOptions =
  row => {

    const level =
      Number(
        row?.activityLevel
      );


    if (
      ![1, 2, 3].includes(
        level
      )
    ) {
      return [];
    }


    return getTargetActivityTypeOptions({
      level
    });
  };


const handleStandardActivityTypeChange = (
  row,
  activityTypeId
) => {

  if (
    !row?.coreId
  ) {
    return;
  }


  const validOptions =
    getStandardActivityTypeOptions(
      row
    );


  const isValid =
    validOptions.some(
      type =>
        type.id ===
        activityTypeId
    );


  if (!isValid) {
    return;
  }


  setStandardActivityTypeOverrides(
    prev => {

      if (
        activityTypeId ===
        row.defaultActivityTypeId
      ) {

        const next = {
          ...prev
        };

        delete next[
          row.coreId
        ];

        return next;
      }


      return {
        ...prev,
        [row.coreId]:
          activityTypeId
      };
    }
  );


  setGeneratedAnnualPlan(null);

  setPlanGenerationErrors([]);

  setActivitiesConfirmed(false);
};





const getStandardTimingLabel =
  timingRule => {

    if (!timingRule) {
      return "Takvim daha sonra atanacak";
    }

    if (
      timingRule.type === "month"
    ) {

      const monthLabels = {
        1: "Ocak",
        2: "Şubat",
        3: "Mart",
        4: "Nisan",
        5: "Mayıs",
        6: "Haziran",
        7: "Temmuz",
        8: "Ağustos",
        9: "Eylül",
        10: "Ekim",
        11: "Kasım",
        12: "Aralık"
      };

      return (
        monthLabels[
          timingRule.month
        ] ||
        `${timingRule.month}. ay`
      );
    }

    const timingLabels = {
      school_start:
        "Sene başı",

      first_term_start:
        "1. dönem başı",

      second_term_start:
        "2. dönem başı",

      school_year_end:
        "Yıl sonu",

      balanced:
        "Yıl içine dengeli dağıtılır"
    };

    return (
      timingLabels[
        timingRule.type
      ] ||
      "Takvim daha sonra atanacak"
    );
  };


const localTarget =
  getTargetById(
    localTargetId
  );


const specialTarget1 =
  getTargetById(
    specialTarget1Id
  );


const specialTarget2 =
  getTargetById(
    specialTarget2Id
  );


const selectedTargets = [

  {
    type: "general",
    shortLabel: "Genel Hedef",
    id: config.generalTarget.id,
    label: config.generalTarget.label,
    developmentArea: null
  },

  {
    type: "local",
    shortLabel: "Yerel Hedef",
    id: localTarget?.id || null,
    label:
      localTarget?.officialLabel ||
      "Yerel Hedef",
    developmentArea:
      localTarget?.developmentArea ||
      null
  },

  {
    type: "special1",
    shortLabel: "Özel Hedef 1",
    id: specialTarget1?.id || null,
    label:
      specialTarget1?.officialLabel ||
      "Özel Hedef 1",
    developmentArea:
      specialTarget1?.developmentArea ||
      null
  },

  {
    type: "special2",
    shortLabel: "Özel Hedef 2",
    id: specialTarget2?.id || null,
    label:
      specialTarget2?.officialLabel ||
      "Özel Hedef 2",
    developmentArea:
      specialTarget2?.developmentArea ||
      null
  }

];


  const specialTargetsDuplicate =
    specialTarget1Id &&
    specialTarget2Id &&
    specialTarget1Id ===
      specialTarget2Id;


  const canContinue =
    schoolLevel &&
    localTargetId &&
    specialTarget1Id &&
    specialTarget2Id &&
    !specialTargetsDuplicate;


const selectedEditableTargetCount =
  [
    localTargetId,
    specialTarget1Id,
    specialTarget2Id
  ].filter(Boolean).length;

const handleAutoTargetResolutionCheck =
  () => {

    if (!canContinue) {
      return;
    }


    const report =
  resolveAutoTargetActivities({
    selectedTargets,

    planningProfileId:
      "regular_school",

    schoolLevel
  });


    const slotReport =
      buildAutoTargetActivitySlots({
        resolutionReport:
          report,

        planningProfileId:
          "regular_school"
      });


    setAutoTargetResolutionReport({
      ...report,

      slotReport
    });
  };


// =========================================================
// OTOMATİK ROBOT - TAM HEDEF KATALOĞU AUDIT
// =========================================================

const handleAutoCatalogAudit =
  () => {

    if (!schoolLevel) {
      return;
    }


    /*
     * EK-2 hedef kataloğundaki bütün aktif
     * hedefleri tek seferde resolver'a gönderiyoruz.
     */
    const auditTargets =
      groupedTargets.flatMap(
        group =>
          (
            Array.isArray(
              group?.targets
            )
              ? group.targets
              : []
          ).map(
            target => ({

              /*
               * Audit sırasında her hedefin
               * targetType değeri benzersiz olmalı.
               */
              type:
                `audit_${target.id}`,

              shortLabel:
                group.label ||
                "Hedef",

              id:
                target.id,

              label:
                target.officialLabel,

              developmentArea:
                target.developmentArea ||
                null

            })
          )
      );


    const report =
      resolveAutoTargetActivities({

        selectedTargets:
          auditTargets,

        planningProfileId:
          "regular_school",

        schoolLevel

      });


    const slotReport =
      buildAutoTargetActivitySlots({

        resolutionReport:
          report,

        planningProfileId:
          "regular_school"

      });


    /*
     * EXPLICIT_MAPPING_NOT_FOUND hata değildir.
     * Exact eşleşmesi olan hedeflerde explicit
     * kurala zaten ihtiyaç yoktur.
     *
     * Burada yalnız gerçek mapping bozukluklarını
     * audit hatası olarak kabul ediyoruz.
     */
    const invalidMappingResolutions =
      (
        report.resolutions ||
        []
      ).filter(
        item =>
          [
            "EXPLICIT_MAPPING_INVALID",
            "EXPLICIT_MAPPING_DEVELOPMENT_AREA_MISMATCH"
          ].includes(
            item.explicitMappingReason
          )
      );


    const blockingUnresolved =
      Array.isArray(
        slotReport
          ?.blockingUnresolved
      )
        ? slotReport
            .blockingUnresolved
        : [];


    const recommendedUnresolved =
      Array.isArray(
        slotReport
          ?.recommendedUnresolved
      )
        ? slotReport
            .recommendedUnresolved
        : [];


    const slotErrors =
      Array.isArray(
        slotReport?.errors
      )
        ? slotReport.errors
        : [];


    const passed =
      blockingUnresolved.length ===
        0 &&
      invalidMappingResolutions.length ===
        0 &&
      slotErrors.length ===
        0;


    setAutoCatalogAuditReport({

      schoolLevel,

      schoolLevelLabel:
        selectedSchoolLevelLabel,

      targetCount:
        auditTargets.length,

      targetAudienceCount:
        auditTargets.length * 3,

      report,

      slotReport,

      blockingUnresolved,

      recommendedUnresolved,

      invalidMappingResolutions,

      slotErrors,

      passed

    });
  };



// =========================================================
// OTOMATİK ROBOT - YILLIK PLANI GERÇEKTEN OLUŞTUR
// =========================================================

const handleGenerateAutoPlan =
  () => {

    if (!canContinue) {
      return;
    }


    // -------------------------------------------------------
    // 1. HEDEFLERİ GÜVENLİ RESMÎ FAALİYETLERE ÇÖZ
    // -------------------------------------------------------

    const report =
      resolveAutoTargetActivities({

        selectedTargets,

        planningProfileId:
          "regular_school",

        schoolLevel

      });


    // -------------------------------------------------------
    // 2. 2 + 2 + 1 ÇALIŞMA SLOTLARINI ÜRET
    // -------------------------------------------------------

    const slotReport =
      buildAutoTargetActivitySlots({

        resolutionReport:
          report,

        planningProfileId:
          "regular_school"

      });


    /*
     * Kullanıcı ayrıca "Uygunluğu Kontrol Et"
     * butonuna basmak zorunda değildir.
     *
     * Otomatik Plan Oluştur her çalışmada
     * resolver ve slot kontrolünü yeniden yapar.
     */
    setAutoTargetResolutionReport({
      ...report,
      slotReport
    });


    // -------------------------------------------------------
    // 3. ZORUNLU EŞLEME / SLOT HATASI VARSA DUR
    // -------------------------------------------------------

    if (!slotReport?.success) {

      const blockingErrors =
        (
          slotReport
            ?.blockingUnresolved ||
          []
        ).map(item => {

          const audienceLabel =
            item.audience ===
              "student"
              ? "Öğrenci"
              : item.audience ===
                  "parent"
                ? "Veli"
                : "Öğretmen";


          return {

            code:
              "AUTO_TARGET_MAPPING_REQUIRED",

            message:
              `"${item.targetLabel}" hedefinde ${audienceLabel} için güvenilir resmî faaliyet eşlemesi bulunamadı.`

          };

        });


      setGeneratedAnnualPlan(
        null
      );


      setPreparedActivityRows(
        []
      );


      setPlanGenerationErrors([
        ...(
          slotReport?.errors ||
          []
        ),
        ...blockingErrors
      ]);


      setActivitiesConfirmed(
        false
      );


      return;
    }


    // -------------------------------------------------------
    // 4. SLOTLARI GERÇEK PLAN SATIRLARINA DÖNÜŞTÜR
    // -------------------------------------------------------

    const autoTargetRows =
      buildAutoTargetActivityRows({

        autoActivitySlots:
          slotReport.slots

      });


    /*
     * Slot builder başarılı görünüp row builder
     * herhangi bir satırı reddederse sessizce
     * eksik plan üretmiyoruz.
     */
    if (
      autoTargetRows.length !==
      slotReport.slots.length
    ) {

      setGeneratedAnnualPlan(
        null
      );


      setPreparedActivityRows(
        []
      );


      setPlanGenerationErrors([
        {
          code:
            "AUTO_TARGET_ROW_BUILD_INCOMPLETE",

          message:
            `Otomatik Robot ${slotReport.slots.length} çalışma slotu üretti ancak yalnızca ${autoTargetRows.length} plan satırı doğrulanabildi. Plan güvenlik nedeniyle oluşturulmadı.`
        }
      ]);


      setActivitiesConfirmed(
        false
      );


      return;
    }


    setPreparedActivityRows(
      autoTargetRows
    );


    // -------------------------------------------------------
    // 5. STANDART/CORE + OTOMATİK HEDEF SATIRLARINI BİRLEŞTİR
    // -------------------------------------------------------

    const annualPlanResult =
      generateAnnualPlanDraft({

        academicYear,

        profileId:
          "regular_school",

        schoolLevel,

        selectedTargets,

        selectedActivityRows:
          autoTargetRows,

        excludedCoreIds:
          excludedStandardCoreIds,

        activityTypeOverrides:
          standardActivityTypeOverrides

      });


    if (
      !annualPlanResult.success
    ) {

      setGeneratedAnnualPlan(
        null
      );


      setPlanGenerationErrors(
        annualPlanResult.errors ||
        []
      );


      setActivitiesConfirmed(
        false
      );


      return;
    }


    // -------------------------------------------------------
    // 6. TAKVİME DAĞIT
    // -------------------------------------------------------

    const scheduleResult =
      schedulePlanRows({

        rows:
          annualPlanResult
            .plan.activities,

        academicYear

      });


    if (
      !scheduleResult.success
    ) {

      setGeneratedAnnualPlan(
        null
      );


      setPlanGenerationErrors(
        scheduleResult.errors ||
        []
      );


      setActivitiesConfirmed(
        false
      );


      return;
    }


    // -------------------------------------------------------
    // 7. SINIF KAPSAMI + MANUEL OVERRIDE'LAR
    // -------------------------------------------------------

    const scheduledAnnualPlan = {

      ...annualPlanResult.plan,

      generationMode:
        "auto_robot",

      activities:
        applyCoverageOverrides(
          applyPlanClassCoverage(
            applyDateOverrides(
              scheduleResult.rows
            )
          )
        ),

      schedule: {

        calendarVersion:
          scheduleResult
            .calendarVersion ||
          null,

        warnings:
          scheduleResult
            .warnings ||
          []

      }

    };


    // -------------------------------------------------------
    // 8. PLAN ÖNİZLEMEYE GEÇ
    // -------------------------------------------------------

    setGeneratedAnnualPlan(
      scheduledAnnualPlan
    );


    setPlanGenerationErrors(
      []
    );


    setActivitiesConfirmed(
      true
    );


    setActiveStep(
      3
    );

  };



const resetActivityStep = () => {

  setShowSummary(false);

  setSelectedActivities(
    createEmptyActivitySelections()
  );

  setSelectedAdditionalActivities([]);

  setAdditionalCategory("");

  setAdditionalSearch("");

setPreparedActivityRows([]);

setGeneratedAnnualPlan(null);

setPlanGenerationErrors([]);

setSelectedActivityTypes({});

setSelectedActivityLevels({});

setSelectedAdditionalActivityTypes({});

setActiveTargetType(
  "general"
);

setActivitiesConfirmed(false);

setActiveStep(1);

setShowStandardActivities(false);

setAutoTargetResolutionReport(null);
};


// =========================================================
// PLANLAMA MODU
// =========================================================

const handlePlanningModeChange =
  nextMode => {

    if (
      nextMode !== "auto" &&
      nextMode !== "manual"
    ) {
      return;
    }


    if (
      nextMode === planningMode
    ) {
      return;
    }


    setPlanningMode(
      nextMode
    );


    /*
     * Önceki modda oluşturulmuş yıllık planı
     * yeni modda yanlışlıkla göstermiyoruz.
     *
     * Hedef seçimleri ve manuel faaliyet seçimleri
     * korunur. Kullanıcı modlar arasında geçiş
     * yaptığında çalışmasını kaybetmez.
     */
    setGeneratedAnnualPlan(
      null
    );

    setPreparedActivityRows(
      []
    );

    setActivitiesConfirmed(
      false
    );

    setPlanGenerationErrors(
      []
    );

    setAutoTargetResolutionReport(
      null
    );

    setAutoCatalogAuditReport(
      null
    );


    setActiveStep(
      showSummary
        ? 2
        : 1
    );
  };


const handleContinue = () => {

  if (!canContinue) {
    return;
  }

  setShowSummary(true);

  setActiveTargetType(
    "general"
  );

  setActivitiesConfirmed(false);
setActiveStep(2);
};


const handleAddActivity = ({
  targetType,
  audience,
  activityId
}) => {

  if (!activityId) {
    return;
  }


  setSelectedActivities(prev => {

    const current =
      prev[targetType]?.[audience] ||
      [];


    if (
      current.includes(
        activityId
      )
    ) {
      return prev;
    }


    return {

      ...prev,

      [targetType]: {

        ...prev[targetType],

        [audience]: [
          ...current,
          activityId
        ]
      }
    };
  });


  setActivitiesConfirmed(false);
};


const handleRemoveActivity = ({
  targetType,
  audience,
  activityId
}) => {

  setSelectedActivities(prev => ({

    ...prev,

    [targetType]: {

      ...prev[targetType],

      [audience]:
        (
          prev[targetType]?.[
            audience
          ] || []
        ).filter(
          id =>
            id !== activityId
        )
    }
  }));


  const typeKey =
    getActivityTypeKey({
      targetType,
      audience,
      activityId
    });

 setSelectedActivityTypes(
  prev => {

    const next = {
      ...prev
    };

    delete next[
      typeKey
    ];

    return next;
  }
);


setSelectedActivityLevels(
  prev => {

    const next = {
      ...prev
    };

    delete next[
      typeKey
    ];

    return next;
  }
);


setActivitiesConfirmed(false);
};

const getAudienceRule = (
  audience
) => {

  return (
    ruleProfile
      ?.minimumLevel1PerTarget
      ?.[audience] || {
        count: 0,
        enforcement: "not_required"
      }
  );
};


const getSelectedActivityIds = (
  targetType,
  audience
) => {

  return (
    selectedActivities
      ?.[targetType]
      ?.[audience] || []
  );
};

const getLevel1ActivityCount = (
  targetType,
  audience
) => {

  const activityIds =
    getSelectedActivityIds(
      targetType,
      audience
    );

  return activityIds.filter(
    activityId => {

      const level =
        getSelectedActivityLevel({
          targetType,
          audience,
          activityId
        });

      return (
        Number(level) === 1
      );
    }
  ).length;
};

const getActivityTypeKey = ({
  targetType,
  audience,
  activityId
}) => {

  return `${targetType}__${audience}__${activityId}`;
};

const getSelectedActivityLevel = ({
  targetType,
  audience,
  activityId
}) => {

  const key =
    getActivityTypeKey({
      targetType,
      audience,
      activityId
    });

  return (
    selectedActivityLevels[
      key
    ] ||
    ""
  );
};


const handleActivityLevelChange = ({
  targetType,
  audience,
  activityId,
  level
}) => {

  const key =
    getActivityTypeKey({
      targetType,
      audience,
      activityId
    });

  setSelectedActivityLevels(
    prev => ({
      ...prev,
      [key]: level
    })
  );

  /*
   * Düzey değiştiğinde eski faaliyet türü
   * artık geçerli olmayabilir.
   */
  setSelectedActivityTypes(
    prev => ({
      ...prev,
      [key]: ""
    })
  );

  setActivitiesConfirmed(false);
};

const getSelectedActivityTypeId = ({
  targetType,
  audience,
  activityId
}) => {

  const key =
    getActivityTypeKey({
      targetType,
      audience,
      activityId
    });

  return (
    selectedActivityTypes[
      key
    ] ||
    ""
  );
};


const handleActivityTypeChange = ({
  targetType,
  audience,
  activityId,
  activityTypeId
}) => {

  const key =
    getActivityTypeKey({
      targetType,
      audience,
      activityId
    });

  setSelectedActivityTypes(
    prev => ({
      ...prev,
      [key]: activityTypeId
    })
  );

  setActivitiesConfirmed(false);
};


const getActivityOptionsForTarget = (
  target,
  audience
) => {

  return getTargetActivityOptions({

    audience,

    developmentArea:
      audience === "student"
        ? target?.developmentArea
        : null
  });
};

const getTargetMebbisCategory = (
  activity
) => {

  if (!activity) {
    return null;
  }

  if (
    activity.audience ===
    "student"
  ) {

    if (
      activity.developmentArea ===
      "social_emotional"
    ) {
      return "student_social_emotional";
    }

    if (
      activity.developmentArea ===
      "academic"
    ) {
      return "student_academic";
    }

    if (
      activity.developmentArea ===
      "career"
    ) {
      return "student_career";
    }
  }

  if (
    activity.audience ===
    "parent"
  ) {
    return "parent_support";
  }

  if (
    activity.audience ===
    "teacher"
  ) {
    return "teacher_support";
  }

  return null;
};


const getTargetMebbisCode = (
  activity
) => {

  const exactCategory =
    getTargetMebbisCategory(
      activity
    );

  const exactCode =
    getExactMebbisCode({
      category: exactCategory,
      officialLabel:
        activity?.officialLabel
    });

  if (exactCode) {
    return exactCode;
  }

  return (
    getMebbisDisplayCode(
      activity
    ) ||
    null
  );
};


const getAdditionalMebbisCode = (
  activity
) => {

  if (!activity) {
    return null;
  }

  const exactCode =
    getExactMebbisCode({
      category:
        activity.category,
      officialLabel:
        activity.officialLabel
    });

  if (exactCode) {
    return exactCode;
  }

  /*
   * Bu kategorilerde kategori kodu,
   * MEBBİS ekranında anlamlı bir
   * yönlendirme kodudur.
   *
   * Bireyi Tanıma ve Sınıf Rehberlik
   * çalışmalarında ise faaliyet/kazanım
   * bazlı özel kod aranır.
   */

  const safeGenericCategories =
    [
      "orientation_monitoring",
      "group_counseling",
      "psychosocial_intervention",
      "consultancy_other",
      "program_management",
      "cooperation_meetings",
      "cooperation_activities",
      "professional_development"
    ];

  if (
    !safeGenericCategories.includes(
      activity.category
    )
  ) {
    return null;
  }

  return (
    getMebbisDisplayCode(
      activity
    ) ||
    null
  );
};


const normalizedAdditionalSchoolLevel =
  schoolLevel ===
  "vocational_high_school"
    ? "high_school"
    : schoolLevel;


const isAdditionalActivityForSchool =
  activity => {

    if (
      !activity?.schoolLevel
    ) {
      return true;
    }

    return (
      activity.schoolLevel ===
      normalizedAdditionalSchoolLevel
    );
  };


const additionalCategories =
  Array.from(
    new Map(
      ADDITIONAL_GROUP_ACTIVITY_CATALOG

        .filter(
          activity =>
            activity.active &&
            isAdditionalActivityForSchool(
              activity
            )
        )

        .map(
          activity => [
            activity.category,
            activity.categoryLabel
          ]
        )
    )
  )

    .map(
      ([id, label]) => ({
        id,
        label
      })
    )

    .sort(
      (a, b) =>
        a.label.localeCompare(
          b.label,
          "tr"
        )
    );


const additionalActivityOptions =
  getAdditionalGroupActivityOptions({
    category:
      additionalCategory ||
      null,

    search:
      additionalSearch
  })

    .filter(
      activity =>
        isAdditionalActivityForSchool(
          activity
        )
    )

    .filter(
      activity =>
        !selectedAdditionalActivities
          .includes(
            activity.id
          )
    )

    .sort(
      (a, b) =>
        a.officialLabel.localeCompare(
          b.officialLabel,
          "tr"
        )
    );


const handleAddAdditionalActivity =
  activityId => {

    if (!activityId) {
      return;
    }

    setSelectedAdditionalActivities(
      prev => {

        if (
          prev.includes(
            activityId
          )
        ) {
          return prev;
        }

        return [
          ...prev,
          activityId
        ];
      }
    );

    setActivitiesConfirmed(false);
  };


const getSelectedAdditionalActivityTypeId =
  activityId => {

    return (
      selectedAdditionalActivityTypes[
        activityId
      ] ||
      ""
    );
  };


const handleAdditionalActivityTypeChange = ({
  activityId,
  activityTypeId
}) => {

  setSelectedAdditionalActivityTypes(
    prev => ({
      ...prev,
      [activityId]:
        activityTypeId
    })
  );

  setActivitiesConfirmed(false);
};



const handleRemoveAdditionalActivity =
  activityId => {

    setSelectedAdditionalActivities(
      prev =>
        prev.filter(
          id =>
            id !== activityId
        )
    );

    setSelectedAdditionalActivityTypes(
      prev => {

        const next = {
          ...prev
        };

        delete next[
          activityId
        ];

        return next;
      }
    );

    setActivitiesConfirmed(false);
  };


const requiredActivitySelectionsComplete =
  selectedTargets.every(target => {

    if (!target.id) {
      return false;
    }


    return [
      "student",
      "parent",
      "teacher"
    ].every(audience => {

      const rule =
        getAudienceRule(
          audience
        );


      // Yalnızca zorunlu kurallar
      // ilerlemeyi bloke eder.
      if (
        rule.enforcement !==
        "required"
      ) {
        return true;
      }


    const selectedCount =
  getLevel1ActivityCount(
    target.type,
    audience
  );


      return (
        selectedCount >=
        Number(rule.count || 0)
      );
    });
  });

const targetActivityDetailsComplete =
  selectedTargets.every(target => {

    return [
      "student",
      "parent",
      "teacher"
    ].every(audience => {

      const activityIds =
        getSelectedActivityIds(
          target.type,
          audience
        );


      return activityIds.every(
        activityId => {

          const rawLevel =
            getSelectedActivityLevel({
              targetType:
                target.type,
              audience,
              activityId
            });


          const level =
            Number(rawLevel);


          const activityTypeId =
            getSelectedActivityTypeId({
              targetType:
                target.type,
              audience,
              activityId
            });


          const activityType =
            getActivityTypeById(
              activityTypeId
            );


          /*
           * Yalnızca:
           *
           * Düzey 1 → Düzey 1 türü
           * Düzey 2 → Düzey 2 türü
           * Düzey 3 → Düzey 3 türü
           *
           * eşleşmesine izin verilir.
           */
          return Boolean(
            [1, 2, 3].includes(level) &&
            activityType &&
            activityType.level === level
          );
        }
      );
    });
  });
const additionalActivityDetailsComplete =
  selectedAdditionalActivities.every(
    activityId => {

      const activity =
        getAdditionalGroupActivityById(
          activityId
        );

      if (!activity) {
        return false;
      }

      const verifiedActivityType =
        getDefaultActivityTypeForAdditionalCategory(
          activity.category
        );

      return Boolean(
        verifiedActivityType
      );
    }
  );


const canConfirmActivities =
  requiredActivitySelectionsComplete &&
  targetActivityDetailsComplete &&
  additionalActivityDetailsComplete;

// =========================================================
// YILLIK PLANI EXCEL'E AKTAR
// =========================================================

const handleExportAnnualPlanExcel =
  async () => {

  if (
    !generatedAnnualPlan ||
    !Array.isArray(
      generatedAnnualPlan.activities
    ) ||
    generatedAnnualPlan.activities.length === 0
  ) {

    alert(
      "Excel'e aktarılacak yıllık plan bulunamadı."
    );

    return;
  }


  // -------------------------------------------------------
  // RENK PALETİ
  // Yazıcı dostu, sade ve resmî görünüm
  // -------------------------------------------------------

  const COLORS = {

    navy:
      "24324A",

    indigo:
      "4F46E5",

    headerFill:
      "E9EDF5",

    sectionFill:
      "F3F4F6",

    alternateFill:
      "F8FAFC",

    border:
      "CBD5E1",

    darkText:
      "1E293B",

    softText:
      "64748B",

    white:
      "FFFFFF"
  };


  // -------------------------------------------------------
  // YARDIMCILAR
  // -------------------------------------------------------

  const formatExcelDate =
    value => {

      if (!value) {
        return "";
      }

      return String(value)
        .split("-")
        .reverse()
        .join(".");
    };


 const getExcelTargetLabel =
  row => {

    if (
      !row?.targetType ||
      !row?.targetLabel
    ) {
      return "HEDEF DIŞI";
    }


    const displayTargetLabel =
      getPlanningTargetDisplayLabel(
        row.targetLabel
      );


    if (
      row.targetType ===
      "general"
    ) {

      return (
        `GENEL HEDEF - ${displayTargetLabel}`
      );
    }


    if (
      row.targetType ===
      "local"
    ) {

      return (
        `YEREL HEDEF - ${displayTargetLabel}`
      );
    }


    if (
      row.targetType ===
        "special1" ||
      row.targetType ===
        "special2"
    ) {

      return (
        `ÖZEL HEDEF - ${displayTargetLabel}`
      );
    }


    return displayTargetLabel;
  };


  const getExcelActivityType =
    row => {

      if (
        !row?.activityTypeLabel
      ) {
        return "";
      }


      return row.activityLevel
        ? `${row.activityTypeLabel} (Düzey ${row.activityLevel})`
        : row.activityTypeLabel;
    };


  const getExcelClassLabel =
    row => {

      if (
        row?.coverage?.mode ===
        "not_applicable"
      ) {
        return "";
      }


      const classLabels =
        Array.isArray(
          row?.coverage?.classLabels
        )
          ? row.coverage.classLabels
          : [];


      if (
        classLabels.length > 0
      ) {

        return classLabels.join(
          ", "
        );
      }


      if (
        row?.coverage?.mode ===
        "all_classes"
      ) {

        return "Tüm Sınıflar";
      }


      return "";
    };


  const getOfficialActivityLabel =
    row => {

      const officialLabel =
        row?.officialLabel ||
        "";


      return row?.mebbisCode
        ? `${row.mebbisCode} - ${officialLabel}`
        : officialLabel;
    };


  const thinBorder = {

    top: {
      style: "thin",
      color: {
        argb:
          COLORS.border
      }
    },

    left: {
      style: "thin",
      color: {
        argb:
          COLORS.border
      }
    },

    bottom: {
      style: "thin",
      color: {
        argb:
          COLORS.border
      }
    },

    right: {
      style: "thin",
      color: {
        argb:
          COLORS.border
      }
    }
  };


  /*
   * Bir aralıktaki bütün hücrelere
   * aynı border'ı uygular.
   */
  const applyBorderToRange =
    (
      worksheet,
      startRow,
      endRow,
      startColumn,
      endColumn
    ) => {

      for (
        let rowIndex =
          startRow;
        rowIndex <=
          endRow;
        rowIndex += 1
      ) {

        for (
          let columnIndex =
            startColumn;
          columnIndex <=
            endColumn;
          columnIndex += 1
        ) {

          worksheet.getCell(
            rowIndex,
            columnIndex
          ).border =
            thinBorder;
        }
      }
    };


  // -------------------------------------------------------
  // WORKBOOK
  // -------------------------------------------------------

  const workbook =
    new ExcelJS.Workbook();


  workbook.creator =
    "Rehber360";

  workbook.created =
    new Date();


  // =======================================================
  // PLAN BİLGİLERİ
  // =======================================================

  const infoSheet =
    workbook.addWorksheet(
      "PLAN BİLGİLERİ",
      {
        views: [
          {
            showGridLines:
              false
          }
        ]
      }
    );


  infoSheet.pageSetup = {

    paperSize: 9,

    orientation:
      "portrait",

    fitToPage:
      true,

    fitToWidth:
      1,

    fitToHeight:
      1,

    horizontalCentered:
      true,

    margins: {
      left: 0.4,
      right: 0.4,
      top: 0.5,
      bottom: 0.5,
      header: 0.2,
      footer: 0.2
    }
  };


  infoSheet.columns = [

    {
      width: 4
    },

    {
      width: 22
    },

    {
      width: 18
    },

    {
      width: 18
    },

    {
      width: 18
    },

    {
      width: 18
    },

    {
      width: 4
    }
  ];


  infoSheet.mergeCells(
    "A1:G1"
  );

  infoSheet.getCell(
    "A1"
  ).value =
    `${academicYear} EĞİTİM ÖĞRETİM YILI`;


  infoSheet.getCell(
    "A1"
  ).font = {

    bold: true,
    size: 15,
    color: {
      argb:
        COLORS.white
    }
  };


  infoSheet.getCell(
    "A1"
  ).fill = {

    type: "pattern",
    pattern: "solid",

    fgColor: {
      argb:
        COLORS.navy
    }
  };


  infoSheet.getCell(
    "A1"
  ).alignment = {

    horizontal:
      "center",

    vertical:
      "middle"
  };


  infoSheet.getRow(
    1
  ).height = 28;


  infoSheet.mergeCells(
    "A2:G2"
  );

  infoSheet.getCell(
    "A2"
  ).value =
    "OKUL REHBERLİK VE PSİKOLOJİK DANIŞMA PROGRAMI";


  infoSheet.getCell(
    "A2"
  ).font = {

    bold: true,
    size: 13,
    color: {
      argb:
        COLORS.darkText
    }
  };


  infoSheet.getCell(
    "A2"
  ).alignment = {

    horizontal:
      "center",

    vertical:
      "middle"
  };


  infoSheet.getRow(
    2
  ).height = 26;


  infoSheet.mergeCells(
    "B4:F4"
  );

  infoSheet.getCell(
    "B4"
  ).value =
    "PLAN BİLGİLERİ";


  infoSheet.getCell(
    "B4"
  ).font = {

    bold: true,
    size: 12,
    color: {
      argb:
        COLORS.navy
    }
  };


  infoSheet.getCell(
    "B4"
  ).fill = {

    type: "pattern",
    pattern: "solid",

    fgColor: {
      argb:
        COLORS.headerFill
    }
  };


  infoSheet.getCell(
    "B4"
  ).alignment = {

    horizontal:
      "center",

    vertical:
      "middle"
  };


  infoSheet.getRow(
    4
  ).height = 24;


  const schoolLevelLabel =
    SCHOOL_LEVELS.find(
      item =>
        item.value ===
        schoolLevel
    )?.label ||
    schoolLevel ||
    "";


  const classStructureLabel =
    planClassOptions.length > 0
      ? planClassOptions
          .map(
            item =>
              item.label
          )
          .join(", ")
      : "";


  const planInfo = [

    [
      "Eğitim Öğretim Yılı",
      academicYear
    ],

    [
      "Okul Kademesi",
      schoolLevelLabel
    ],

    [
      "Genel Hedef",
      config?.generalTarget?.label ||
      ""
    ],

    [
  "Yerel Hedef",
  getPlanningTargetDisplayLabel(
    localTarget?.officialLabel || ""
  )
],

[
  "Özel Hedef 1",
  getPlanningTargetDisplayLabel(
    specialTarget1?.officialLabel || ""
  )
],

[
  "Özel Hedef 2",
  getPlanningTargetDisplayLabel(
    specialTarget2?.officialLabel || ""
  )
],

    [
      "Sınıf / Şube Yapısı",
      classStructureLabel
    ],

    [
      "Toplam Plan Faaliyeti",
      generatedAnnualPlan
        .activities
        .length
    ]
  ];


  planInfo.forEach(
    (
      item,
      index
    ) => {

      const rowNumber =
        5 + index;


      infoSheet.mergeCells(
        `B${rowNumber}:C${rowNumber}`
      );

      infoSheet.mergeCells(
        `D${rowNumber}:F${rowNumber}`
      );


      const labelCell =
        infoSheet.getCell(
          `B${rowNumber}`
        );


      const valueCell =
        infoSheet.getCell(
          `D${rowNumber}`
        );


      labelCell.value =
        item[0];

      valueCell.value =
        item[1];


      labelCell.font = {

        bold: true,
        size: 10,
        color: {
          argb:
            COLORS.darkText
        }
      };


      labelCell.fill = {

        type: "pattern",
        pattern: "solid",

        fgColor: {
          argb:
            COLORS.sectionFill
        }
      };


      valueCell.font = {

        size: 10,
        color: {
          argb:
            COLORS.darkText
        }
      };


      labelCell.alignment = {
        vertical:
          "middle",
        wrapText:
          true
      };


      valueCell.alignment = {
        vertical:
          "middle",
        wrapText:
          true
      };


      infoSheet.getRow(
        rowNumber
      ).height =
        rowNumber === 11
          ? 34
          : 25;


      applyBorderToRange(
        infoSheet,
        rowNumber,
        rowNumber,
        2,
        6
      );
    }
  );


    // =======================================================
  // KOMİSYON
  // Okul kademesine göre Rehberlik Hizmetleri
  // Yürütme Komisyonu görev dağılımını oluşturur.
  // =======================================================

  const commissionConfigBySchoolLevel = {

    preschool: {
      schoolTypeLabel: "ANAOKULU",

      roles: [
        "Okul Müdürü",
        "Müdür Yardımcısı",
        "Yaş Grubu Öğrt.",
        "Yaş Grubu Öğrt.",
        "Yaş Grubu Öğrt.",
        "Yaş Grubu Öğrt.",
        "Okul Aile Birliği Başkanı"
      ]
    },

    primary: {
      schoolTypeLabel: "İLKOKULU",

      roles: [
        "Okul Müdürü",
        "Müdür Yardımcısı",
        "Sınıf Reh. Öğrt.",
        "Sınıf Reh. Öğrt.",
        "Sınıf Reh. Öğrt.",
        "Sınıf Reh. Öğrt.",
        "Okul Aile Bir. Temsilcisi",
        "Öğrenci Davranışları Değerlendirme Kurulu"
      ]
    },

    middle_school: {
      schoolTypeLabel: "ORTAOKULU",

      roles: [
        "Okul Müdürü",
        "Müdür Yardımcısı",
        "5. Sınıf Reh. Öğrt.",
        "6. Sınıf Reh. Öğrt.",
        "7. Sınıf Reh. Öğrt.",
        "8. Sınıf Reh. Öğrt.",
        "Öğrenci Davranışları Değerlendirme Kurulu",
        "Okul Aile Bir. Temsilcisi"
      ]
    },

    high_school: {
      schoolTypeLabel: "LİSESİ",

      roles: [
        "Okul Müdürü",
        "Müdür Yardımcısı",
        "9. Sınıf Reh. Öğrt.",
        "10. Sınıf Reh. Öğrt.",
        "11. Sınıf Reh. Öğrt.",
        "12. Sınıf Reh. Öğrt.",
        "Öğrenci Ödül ve Disiplin Kurulu Üyesi",
        "Okul Aile Bir. Temsilcisi"
      ]
    },

    vocational_high_school: {
      schoolTypeLabel:
        "MESLEKİ VE TEKNİK ANADOLU LİSESİ",

      roles: [
        "Okul Müdürü",
        "Müdür Yardımcısı",
        "9. Sınıf Reh. Öğrt.",
        "10. Sınıf Reh. Öğrt.",
        "11. Sınıf Reh. Öğrt.",
        "12. Sınıf Reh. Öğrt.",
        "Öğrenci Ödül ve Disiplin Kurulu Üyesi",
        "Okul Aile Bir. Temsilcisi"
      ]
    }
  };


  const commissionConfig =
    commissionConfigBySchoolLevel[
      schoolLevel
    ] || null;


  if (commissionConfig) {

    const commissionSheet =
      workbook.addWorksheet(
        "KOMİSYON",
        {
          views: [
            {
              showGridLines: false
            }
          ]
        }
      );


    commissionSheet.pageSetup = {

      paperSize: 9,

      orientation:
        "portrait",

      fitToPage:
        true,

      fitToWidth:
        1,

      fitToHeight:
        1,

      horizontalCentered:
        true,

      margins: {
        left: 0.35,
        right: 0.35,
        top: 0.45,
        bottom: 0.45,
        header: 0.15,
        footer: 0.2
      }
    };


    commissionSheet.headerFooter = {
      oddFooter:
        "&LRehber360&R&P / &N"
    };


    commissionSheet.columns = [
      { width: 7 },
      { width: 12 },
      { width: 12 },
      { width: 12 },
      { width: 12 },
      { width: 12 },
      { width: 15 },
      { width: 15 },
      { width: 15 },
      { width: 12 },
      { width: 12 },
      { width: 12 },
      { width: 12 }
    ];


    commissionSheet.mergeCells(
      "A1:K1"
    );

    commissionSheet.mergeCells(
      "L1:M1"
    );

    commissionSheet.mergeCells(
      "A2:M2"
    );

    commissionSheet.mergeCells(
      "A3:A4"
    );

    commissionSheet.mergeCells(
      "B3:F4"
    );

    commissionSheet.mergeCells(
      "G3:I4"
    );

    commissionSheet.mergeCells(
      "J3:M4"
    );


    commissionSheet.getCell(
      "A1"
    ).value =
      `${academicYear} EĞİTİM ÖĞRETİM YILI`;

    commissionSheet.getCell(
      "L1"
    ).value =
      "TARİH : ……………";

       commissionSheet.getCell(
      "A2"
    ).value =
      `………………………... ${commissionConfig.schoolTypeLabel} REHBERLİK HİZMETLERİ YÜRÜTME KOMİSYONU`;


    [
      "A1",
      "L1",
      "A2"
    ].forEach(cellAddress => {

      const cell =
        commissionSheet.getCell(
          cellAddress
        );

      cell.font = {
        bold: true,
        size:
          cellAddress === "A2"
            ? 12
            : 11,
        color: {
          argb:
            COLORS.darkText
        }
      };

      cell.alignment = {
        horizontal:
          "center",
        vertical:
          "middle",
        wrapText:
          true
      };
    });


    commissionSheet.getCell(
      "A3"
    ).value =
      "Sıra";

    commissionSheet.getCell(
      "B3"
    ).value =
      "ADI SOYADI";

    commissionSheet.getCell(
      "G3"
    ).value =
      "GÖREVİ";

    commissionSheet.getCell(
      "J3"
    ).value =
      "İMZA";


    [
      "A3",
      "B3",
      "G3",
      "J3"
    ].forEach(cellAddress => {

      const cell =
        commissionSheet.getCell(
          cellAddress
        );

      cell.font = {
        bold: true,
        size: 10,
        color: {
          argb:
            COLORS.navy
        }
      };

      cell.fill = {
        type: "pattern",
        pattern: "solid",
        fgColor: {
          argb:
            COLORS.headerFill
        }
      };

      cell.alignment = {
        horizontal:
          "center",
        vertical:
          "middle",
        wrapText:
          true
      };
    });


       const commissionRoles =
      commissionConfig.roles;


    commissionRoles.forEach(
      (role, index) => {

        const rowNumber =
          index + 5;

        commissionSheet.mergeCells(
          `B${rowNumber}:F${rowNumber}`
        );

        commissionSheet.mergeCells(
          `G${rowNumber}:I${rowNumber}`
        );

        commissionSheet.mergeCells(
          `J${rowNumber}:M${rowNumber}`
        );

        commissionSheet.getCell(
          `A${rowNumber}`
        ).value =
          index + 1;

        commissionSheet.getCell(
          `G${rowNumber}`
        ).value =
          role;

        commissionSheet.getCell(
          `A${rowNumber}`
        ).alignment = {
          horizontal: "center",
          vertical: "middle"
        };

        commissionSheet.getCell(
          `G${rowNumber}`
        ).alignment = {
          vertical: "middle",
          wrapText: true
        };

        commissionSheet.getRow(
          rowNumber
        ).height =
          27;
      }
    );


    applyBorderToRange(
      commissionSheet,
      3,
      12,
      1,
      13
    );


    commissionSheet.mergeCells(
      "J14:M19"
    );

    commissionSheet.getCell(
      "J14"
    ).value =
      "……………………………………………..\nOKUL MÜDÜRÜ";

    commissionSheet.getCell(
      "J14"
    ).font = {
      bold: true,
      size: 10,
      color: {
        argb:
          COLORS.darkText
      }
    };

    commissionSheet.getCell(
      "J14"
    ).alignment = {
      horizontal:
        "center",
      vertical:
        "bottom",
      wrapText:
        true
    };


    commissionSheet.getRow(1).height =
      25;

    commissionSheet.getRow(2).height =
      28;

    commissionSheet.getRow(3).height =
      18;

    commissionSheet.getRow(4).height =
      18;


    commissionSheet.pageSetup.printArea =
      "A1:M19";
  }



  // =======================================================
  // AYLIK SAYFALAR
  // =======================================================

  const planningMonths = [

    {
      number: 9,
      name: "EYLÜL"
    },

    {
      number: 10,
      name: "EKİM"
    },

    {
      number: 11,
      name: "KASIM"
    },

    {
      number: 12,
      name: "ARALIK"
    },

    {
      number: 1,
      name: "OCAK"
    },

    {
      number: 2,
      name: "ŞUBAT"
    },

    {
      number: 3,
      name: "MART"
    },

    {
      number: 4,
      name: "NİSAN"
    },

    {
      number: 5,
      name: "MAYIS"
    },

    {
      number: 6,
      name: "HAZİRAN"
    }
  ];


  planningMonths.forEach(
    monthConfig => {

      const worksheet =
        workbook.addWorksheet(
          monthConfig.name,
          {
            views: [
              {
                state:
                  "frozen",

                ySplit:
                  4,

                showGridLines:
                  false
              }
            ]
          }
        );


      /*
       * A4 yatay.
       * Bir sayfa genişlikte, yükseklik
       * gerektiği kadar devam edebilir.
       */
      worksheet.pageSetup = {

        paperSize: 9,

        orientation:
          "landscape",

        fitToPage:
          true,

        fitToWidth:
          1,

        fitToHeight:
          0,

        horizontalCentered:
          true,

        printTitlesRow:
          "1:4",

        margins: {
          left: 0.25,
          right: 0.25,
          top: 0.4,
          bottom: 0.4,
          header: 0.15,
          footer: 0.2
        }
      };


      worksheet.headerFooter = {

        oddFooter:
          "&LRehber360&R&P / &N"
      };


      worksheet.columns = [

        {
          key: "order",
          width: 7
        },

        {
          key: "activity",
          width: 38
        },

        {
          key: "activityType",
          width: 34
        },

        {
          key: "startDate",
          width: 15
        },

        {
          key: "endDate",
          width: 15
        },

        {
          key: "target",
          width: 29
        },

        {
          key: "classes",
          width: 32
        }
      ];


      // -----------------------------------------------
      // ÜST BAŞLIK
      // -----------------------------------------------

      worksheet.mergeCells(
        "A1:G1"
      );

      worksheet.getCell(
        "A1"
      ).value =
        `${academicYear} EĞİTİM ÖĞRETİM YILI`;


      worksheet.getCell(
        "A1"
      ).font = {

        bold: true,
        size: 13,
        color: {
          argb:
            COLORS.white
        }
      };


      worksheet.getCell(
        "A1"
      ).fill = {

        type: "pattern",
        pattern: "solid",

        fgColor: {
          argb:
            COLORS.navy
        }
      };


      worksheet.getCell(
        "A1"
      ).alignment = {

        horizontal:
          "center",

        vertical:
          "middle"
      };


      worksheet.getRow(
        1
      ).height = 25;


      worksheet.mergeCells(
        "A2:G2"
      );

      worksheet.getCell(
        "A2"
      ).value =
        `${monthConfig.name} AYI OKUL REHBERLİK VE PSİKOLOJİK DANIŞMA PROGRAMI`;


      worksheet.getCell(
        "A2"
      ).font = {

        bold: true,
        size: 12,
        color: {
          argb:
            COLORS.darkText
        }
      };


      worksheet.getCell(
        "A2"
      ).fill = {

        type: "pattern",
        pattern: "solid",

        fgColor: {
          argb:
            COLORS.headerFill
        }
      };


      worksheet.getCell(
        "A2"
      ).alignment = {

        horizontal:
          "center",

        vertical:
          "middle"
      };


      worksheet.getRow(
        2
      ).height = 24;


      // -----------------------------------------------
      // TABLO BAŞLIKLARI
      // -----------------------------------------------

      const headerRow =
        worksheet.getRow(
          4
        );


      headerRow.values = [

        "SIRA",

        "YAPILACAK ÇALIŞMA",

        "FAALİYET TÜRÜ",

        "BAŞLANGIÇ TARİHİ",

        "BİTİŞ TARİHİ",

        "HEDEF",

        "ÇALIŞMADAKİ SINIFLAR"
      ];


      headerRow.height =
        32;


      headerRow.eachCell(
        cell => {

          cell.font = {

            bold: true,
            size: 9,
            color: {
              argb:
                COLORS.white
            }
          };


          cell.fill = {

            type: "pattern",
            pattern: "solid",

            fgColor: {
              argb:
                COLORS.navy
            }
          };


          cell.alignment = {

            horizontal:
              "center",

            vertical:
              "middle",

            wrapText:
              true
          };


          cell.border =
            thinBorder;
        }
      );


      // -----------------------------------------------
      // AYIN FAALİYETLERİ
      // -----------------------------------------------

      const monthRows =
        generatedAnnualPlan
          .activities
          .filter(
            row =>
              Number(
                row.month
              ) ===
              monthConfig.number
          )
          .sort(
            (a, b) =>
              (
                a.startDate ||
                "9999-12-31"
              ).localeCompare(
                b.startDate ||
                "9999-12-31"
              )
          );


      monthRows.forEach(
        (
          row,
          index
        ) => {

          const excelRow =
            worksheet.addRow({

              order:
                index + 1,

              activity:
                getOfficialActivityLabel(
                  row
                ),

              activityType:
                getExcelActivityType(
                  row
                ),

              startDate:
                formatExcelDate(
                  row.startDate
                ),

              endDate:
                formatExcelDate(
                  row.endDate
                ),

              target:
                getExcelTargetLabel(
                  row
                ),

              classes:
                getExcelClassLabel(
                  row
                )
            });


          /*
           * Uzun metinlerde yaklaşık yeterli
           * satır yüksekliği.
           */
          const longestText =
            Math.max(

              String(
                getOfficialActivityLabel(
                  row
                )
              ).length,

              String(
                getExcelActivityType(
                  row
                )
              ).length,

              String(
                getExcelTargetLabel(
                  row
                )
              ).length,

              String(
                getExcelClassLabel(
                  row
                )
              ).length
            );


          excelRow.height =
            longestText > 100
              ? 64
              : longestText > 60
                ? 52
                : longestText > 35
                  ? 42
                  : 32;


          excelRow.eachCell(
            {
              includeEmpty:
                true
            },

            cell => {

              cell.font = {

                size: 9,
                color: {
                  argb:
                    COLORS.darkText
                }
              };


              cell.alignment = {

                vertical:
                  "top",

                wrapText:
                  true
              };


              cell.border =
                thinBorder;
            }
          );


          /*
           * Sıra ve tarihler ortada.
           */
          excelRow.getCell(
            1
          ).alignment = {

            horizontal:
              "center",

            vertical:
              "middle"
          };


          excelRow.getCell(
            4
          ).alignment = {

            horizontal:
              "center",

            vertical:
              "middle",

            wrapText:
              true
          };


          excelRow.getCell(
            5
          ).alignment = {

            horizontal:
              "center",

            vertical:
              "middle",

            wrapText:
              true
          };


          /*
           * Çok hafif zebra görünümü.
           */
          if (
            index % 2 === 1
          ) {

            excelRow.eachCell(
              {
                includeEmpty:
                  true
              },

              cell => {

                cell.fill = {

                  type:
                    "pattern",

                  pattern:
                    "solid",

                  fgColor: {
                    argb:
                      COLORS.alternateFill
                  }
                };
              }
            );
          }
        }
      );


      worksheet.autoFilter = {

        from:
          "A4",

        to:
          "G4"
      };


      /*
       * Yazdırılacak alan.
       */
      const lastRow =
        Math.max(
          4,
          worksheet.rowCount
        );


      worksheet.pageSetup.printArea =
        `A1:G${lastRow}`;
    }
  );


  // =======================================================
  // DOSYAYI TARAYICIDAN İNDİR
  // =======================================================

  const buffer =
    await workbook.xlsx
      .writeBuffer();


  const blob =
    new Blob(
      [buffer],
      {
        type:
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
      }
    );


  const url =
    window.URL
      .createObjectURL(
        blob
      );


  const link =
    document.createElement(
      "a"
    );


  const safeAcademicYear =
    String(
      academicYear ||
      "Yillik_Plan"
    )
      .replace(
        /[^0-9A-Za-zÇĞİÖŞÜçğıöşü_-]/g,
        "_"
      );


  link.href =
    url;

  link.download =
    `Rehber360_Okul_RPD_Programi_${safeAcademicYear}.xlsx`;


  document.body
    .appendChild(
      link
    );


  link.click();


  link.remove();


  window.URL
    .revokeObjectURL(
      url
    );
};

const handleConfirmActivities = () => {

 if (
  !canConfirmActivities
) {
  return;
}

  const prepared =
  buildSelectedActivityRows({

    selectedActivities,

    selectedAdditionalActivities,

    selectedTargets,

    selectedActivityLevels,

    selectedActivityTypes,

    selectedAdditionalActivityTypes

  });

  setPreparedActivityRows(
  prepared.allRows
);


const annualPlanResult =
  generateAnnualPlanDraft({

    academicYear,

    profileId:
      "regular_school",

    schoolLevel,

    selectedTargets,

    selectedActivityRows:
      prepared.allRows,

        excludedCoreIds:
      excludedStandardCoreIds,

    activityTypeOverrides:
      standardActivityTypeOverrides

  });


if (
  !annualPlanResult.success
) {

  setGeneratedAnnualPlan(
    null
  );

  setPlanGenerationErrors(
    annualPlanResult.errors ||
    []
  );

  setActivitiesConfirmed(
    false
  );

  return;
}


/*
 * ---------------------------------------------------------
 * TAKVİME DAĞIT
 * ---------------------------------------------------------
 *
 * Annual plan motoru faaliyetleri oluşturur.
 * Scheduler yalnızca mevcut faaliyetlere tarih atar.
 */
const scheduleResult =
  schedulePlanRows({

    rows:
      annualPlanResult.plan.activities,

    academicYear

  });


if (
  !scheduleResult.success
) {

  setGeneratedAnnualPlan(
    null
  );

  setPlanGenerationErrors(
    scheduleResult.errors ||
    []
  );

  setActivitiesConfirmed(
    false
  );

  return;
}


const scheduledAnnualPlan = {

  ...annualPlanResult.plan,

  activities:
  applyCoverageOverrides(
    applyPlanClassCoverage(
      applyDateOverrides(
        scheduleResult.rows
      )
    )
  ),

  schedule: {

    calendarVersion:
      scheduleResult.calendarVersion ||
      null,

    warnings:
      scheduleResult.warnings ||
      []

  }

};


setGeneratedAnnualPlan(
  scheduledAnnualPlan
);

setPlanGenerationErrors([]);

setActivitiesConfirmed(
  true
);

setActiveStep(3);};

  const TargetSelect = ({
    label,
    value,
    onChange,
    helpText
  }) => (

    <div>
      <label className="block text-sm font-black text-slate-700 mb-2">
        {label}
      </label>

      <select
        value={value}
        onChange={e =>
          onChange(e.target.value)
        }
        className="w-full px-4 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
      >
        <option value="">
          Hedef seçin
        </option>

        {groupedTargets.map(group => (

          <optgroup
            key={group.id}
            label={group.label}
          >

            {group.targets.map(target => (

              <option
                key={target.id}
                value={target.id}
              >
                {getPlanningTargetDisplayLabel(
  target.officialLabel
)}
              </option>

            ))}

          </optgroup>

        ))}

      </select>

      {helpText && (
        <p className="mt-2 text-xs text-slate-400 font-medium">
          {helpText}
        </p>
      )}
    </div>
  );

  const AudienceActivityCard = ({
  target,
  audience
}) => {

  const rule =
    getAudienceRule(
      audience
    );


  const selectedIds =
    getSelectedActivityIds(
      target.type,
      audience
    );


  const allOptions =
    getActivityOptionsForTarget(
      target,
      audience
    );


  const availableOptions =
    allOptions.filter(
      activity =>
        !selectedIds.includes(
          activity.id
        )
    );


  const requiredCount =
  Number(
    rule.count || 0
  );


const level1Count =
  getLevel1ActivityCount(
    target.type,
    audience
  );


const complete =
  level1Count >=
  requiredCount;


  const isRecommended =
    rule.enforcement ===
    "recommended";


  const audienceLabel =
    audience === "student"
      ? "Öğrenci"
      : audience === "parent"
        ? "Veli"
        : "Öğretmen";


    const AudienceIcon =
    audience === "student"
      ? GraduationCap
      : audience === "parent"
        ? Users
        : User;


  return (

    <div className="bg-white border border-slate-200 rounded-3xl p-5 shadow-sm">

      {/* BAŞLIK */}

      <div className="flex items-start justify-between gap-3">

        <div className="flex items-center gap-3">

          <div className="h-10 w-10 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">

            <AudienceIcon className="h-5 w-5" />

          </div>


          <div>

            <h4 className="font-black text-slate-800">
  {audienceLabel}
</h4>

<p className="text-xs text-slate-400 mt-0.5">
  Hedef faaliyetleri
</p>

          </div>

        </div>


        <div
          className={`text-[10px] font-black uppercase tracking-wider px-2.5 py-1 rounded-full ${
            complete
              ? "bg-emerald-50 text-emerald-700"
              : isRecommended
                ? "bg-amber-50 text-amber-700"
                : "bg-rose-50 text-rose-700"
          }`}
        >

         {isRecommended
  ? `${level1Count}/${requiredCount} Önerilen`
  : `${level1Count}/${requiredCount}`}

        </div>

      </div>


      {/* AÇIKLAMA */}

      <p className="text-xs text-slate-500 mt-4 leading-relaxed">

        {isRecommended
          ? `Bu hedef için en az ${requiredCount} öğretmen faaliyeti önerilir.`
          : `Bu hedef için en az ${requiredCount} ${audienceLabel.toLocaleLowerCase("tr-TR")} faaliyeti seçilmelidir.`}

      </p>


      {/* FAALİYET EKLE */}

      <select

        value=""

        onChange={e => {

          handleAddActivity({

            targetType:
              target.type,

            audience,

            activityId:
              e.target.value
          });
        }}

        className="w-full mt-4 px-4 py-3 bg-slate-50 border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"

      >

        <option value="">
          + Resmî faaliyet ekle
        </option>


       {availableOptions.map(
  activity => {

    const mebbisCode =
      getTargetMebbisCode(
        activity
      );

    return (

      <option
        key={activity.id}
        value={activity.id}
      >
        {mebbisCode
          ? `[${mebbisCode}] `
          : ""}
        {activity.officialLabel}
      </option>

    );
  }
)}

      </select>


      {/* SEÇİLENLER */}

      <div className="space-y-2 mt-4">

        {selectedIds.length === 0 ? (

          <div className="border border-dashed border-slate-200 rounded-2xl px-4 py-4 text-xs text-slate-400 text-center">

            Henüz faaliyet seçilmedi.

          </div>

        ) : (

          selectedIds.map(
            activityId => {

              const activity =
                getTargetActivityById(
                  activityId
                );


              if (!activity) {
                return null;
              }


const mebbisCode =
  getTargetMebbisCode(
    activity
  );


              return (

                <div
                  key={activityId}
                  className="flex items-start justify-between gap-3 bg-slate-50 border border-slate-100 rounded-2xl px-4 py-3"
                >

                  <div>

                    <div className="flex items-center gap-2 flex-wrap">

  {mebbisCode && (

    <span className="text-[10px] font-black bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md">
      {mebbisCode}
    </span>

  )}

  <p className="text-sm font-bold text-slate-700 leading-snug">
  {activity.officialLabel}
</p>

</div>

<p className="text-[10px] text-slate-400 mt-1 font-semibold">
  Resmî Faaliyet
  {" • "}
  {getSelectedActivityLevel({
    targetType:
      target.type,
    audience,
    activityId
  })
    ? `Düzey ${getSelectedActivityLevel({
        targetType:
          target.type,
        audience,
        activityId
      })}`
    : "Düzey seçilmedi"}
</p>

<div className="mt-3 grid gap-3">

  {/* FAALİYET DÜZEYİ */}

  <div>

    <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">
      Faaliyet Düzeyi
    </label>

    <select
      value={
        getSelectedActivityLevel({
          targetType:
            target.type,
          audience,
          activityId
        })
      }
      onChange={e =>
        handleActivityLevelChange({
          targetType:
            target.type,
          audience,
          activityId,
          level:
            e.target.value
        })
      }
      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
    >

      <option value="">
        Faaliyet düzeyi seçin
      </option>

      <option value="1">
        Düzey 1
      </option>

      <option value="2">
        Düzey 2
      </option>

      <option value="3">
        Düzey 3
      </option>

    </select>

  </div>


  {/* FAALİYET TÜRÜ */}

  <div>

    <label className="block text-[10px] font-black uppercase tracking-wider text-slate-400 mb-1.5">
      Faaliyet Türü
    </label>

    <select
      value={
        getSelectedActivityTypeId({
          targetType:
            target.type,
          audience,
          activityId
        })
      }
      disabled={
        !getSelectedActivityLevel({
          targetType:
            target.type,
          audience,
          activityId
        })
      }
      onChange={e =>
        handleActivityTypeChange({
          targetType:
            target.type,
          audience,
          activityId,
          activityTypeId:
            e.target.value
        })
      }
      className="w-full px-3 py-2 bg-white border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:text-slate-400"
    >

      <option value="">
        Faaliyet türü seçin
      </option>

      {(() => {

        const selectedLevel =
          getSelectedActivityLevel({
            targetType:
              target.type,
            audience,
            activityId
          });

        if (!selectedLevel) {
          return null;
        }

        return getTargetActivityTypeOptions({
          level:
            Number(
              selectedLevel
            )
        }).map(
          type => (

            <option
              key={type.id}
              value={type.id}
            >
              {type.shortLabel}
            </option>

          )
        );
      })()}

    </select>

  </div>

</div>
                  </div>


                  <button

                    type="button"

                    onClick={() =>
                      handleRemoveActivity({

                        targetType:
                          target.type,

                        audience,

                        activityId
                      })
                    }

                    className="h-7 w-7 rounded-lg bg-white border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center shrink-0 transition"

                    title="Faaliyeti kaldır"

                  >

                    <X className="h-3.5 w-3.5" />

                  </button>

                </div>

              );
            }
          )

        )}

      </div>

    </div>
  );
};


  return (

    <div className="max-w-6xl mx-auto space-y-6">

      {/* BAŞLIK */}

      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

        <div>

          <div className="flex items-center gap-2 text-indigo-600 font-bold text-xs uppercase tracking-widest mb-2">
            <ClipboardList className="h-4 w-4" />
            Planlama Merkezi
          </div>

          <h1 className="text-3xl font-black text-slate-900">
            Okul RPD Planı Oluştur
          </h1>

          <p className="text-slate-500 mt-2 max-w-2xl">
            Resmî hedef ve faaliyet havuzunu kullanarak
            okulunuz için yıllık rehberlik planınızı
            adım adım hazırlayın.
          </p>

        </div>


        <div className="bg-white border border-slate-200 rounded-2xl px-5 py-3 shadow-sm">

          <p className="text-[10px] uppercase tracking-widest font-black text-slate-400">
            Eğitim Öğretim Yılı
          </p>

          <p className="font-black text-slate-800 mt-1 flex items-center gap-2">
            <CalendarDays className="h-4 w-4 text-indigo-500" />
            {academicYear}
          </p>

        </div>

      </div>



     {/* PLANLAMA MODU */}

<div className="grid md:grid-cols-2 gap-3">

  <button
    type="button"
    onClick={() =>
      handlePlanningModeChange(
        "auto"
      )
    }
    className={`text-left rounded-2xl border p-4 md:p-5 transition min-h-[138px] ${
      planningMode === "auto"
        ? "bg-indigo-600 border-indigo-600 text-white shadow-md shadow-indigo-100"
        : "bg-white border-slate-200 text-slate-800 hover:border-indigo-300 hover:shadow-sm"
    }`}
  >

    <div className="flex items-start gap-4">

      <div
        className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
          planningMode === "auto"
            ? "bg-white/15 text-white"
            : "bg-indigo-50 text-indigo-600"
        }`}
      >
        <Target className="h-5 w-5" />
      </div>


      <div className="flex-1">

        <div className="flex items-center justify-between gap-3">

          <h3 className="font-black text-base md:text-lg">
            Otomatik Plan
          </h3>

          {planningMode === "auto" && (

            <span className="text-[8px] font-black uppercase tracking-widest bg-white/15 px-2 py-1 rounded-full shrink-0">
              Seçili
            </span>

          )}

        </div>


        <p
          className={`text-xs mt-2 leading-relaxed ${
            planningMode === "auto"
              ? "text-indigo-100"
              : "text-slate-500"
          }`}
        >
          Hedeflerinizi seçin; Rehber360 resmî
          faaliyetleri, standart çalışmaları ve
          yıllık takvimi otomatik hazırlasın.
        </p>


        <p
          className={`text-[10px] font-black mt-3 ${
            planningMode === "auto"
              ? "text-white"
              : "text-indigo-600"
          }`}
        >
          Hızlı ve önerilen yöntem
        </p>

      </div>

    </div>

  </button>


  <button
    type="button"
    onClick={() =>
      handlePlanningModeChange(
        "manual"
      )
    }
    className={`text-left rounded-2xl border p-4 md:p-5 transition min-h-[138px] ${
      planningMode === "manual"
        ? "bg-slate-900 border-slate-900 text-white shadow-md"
        : "bg-white border-slate-200 text-slate-800 hover:border-slate-400 hover:shadow-sm"
    }`}
  >

    <div className="flex items-start gap-4">

      <div
        className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${
          planningMode === "manual"
            ? "bg-white/10 text-white"
            : "bg-slate-100 text-slate-600"
        }`}
      >
        <ClipboardList className="h-5 w-5" />
      </div>


      <div className="flex-1">

        <div className="flex items-center justify-between gap-3">

          <h3 className="font-black text-base md:text-lg">
            Manuel Plan
          </h3>

          {planningMode === "manual" && (

            <span className="text-[8px] font-black uppercase tracking-widest bg-white/10 px-2 py-1 rounded-full shrink-0">
              Seçili
            </span>

          )}

        </div>


        <p
          className={`text-xs mt-2 leading-relaxed ${
            planningMode === "manual"
              ? "text-slate-300"
              : "text-slate-500"
          }`}
        >
          Faaliyetleri, düzeyleri ve faaliyet
          türlerini kendiniz belirleyerek plan
          üzerinde daha ayrıntılı kontrol sağlayın.
        </p>


        <p
          className={`text-[10px] font-black mt-3 ${
            planningMode === "manual"
              ? "text-white"
              : "text-slate-600"
          }`}
        >
          Ayrıntılı kontrol
        </p>

      </div>

    </div>

  </button>

</div>


      {/* PLANLAMA SEKMELERİ */}

<div className="bg-white border border-slate-200 rounded-3xl p-2 shadow-sm">

 <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-2">

    {[
      {
        id: 1,
        label: "Plan & Hedefler",
        description:
          "Okul ve hedef yapısı",
        enabled: true
      },
            {
        id: 2,
        label:
          planningMode === "auto"
            ? "Otomatik Plan"
            : "Faaliyetler",

        description:
          planningMode === "auto"
            ? "Rehber360 planı oluştursun"
            : "Hedef ve ek çalışmalar",

        enabled:
          Boolean(showSummary)
      },
     {
  id: 3,
  label: "Sınıf & Şubeler",
  description:
    "Plan kapsamını belirleyin",
  enabled:
    Boolean(
      activitiesConfirmed &&
      generatedAnnualPlan
    )
},
{
  id: 4,
  label: "Plan Önizleme",
  description:
    "Kontrol ve dışa aktar",
  enabled:
    Boolean(
      activitiesConfirmed &&
      generatedAnnualPlan
    )
}
    ].map(step => {

      const active =
        activeStep === step.id;

     const completed =
  step.id === 1
    ? Boolean(showSummary)
    : step.id === 2
      ? Boolean(
          activitiesConfirmed &&
          generatedAnnualPlan
        )
      : step.id === 3
        ? Boolean(
            activeStep === 4 ||
            planClassOptions.length > 0
          )
        : false;

      return (

        <button
          key={step.id}
          type="button"
          disabled={!step.enabled}
          onClick={() => {
            if (step.enabled) {
              setActiveStep(
                step.id
              );
            }
          }}
          className={`text-left rounded-2xl px-4 py-3.5 border transition ${
            active
              ? "bg-indigo-600 border-indigo-600 text-white shadow-md"
              : completed
                ? "bg-emerald-50 border-emerald-200 text-emerald-900"
                : step.enabled
                  ? "bg-white border-transparent hover:bg-slate-50 text-slate-700"
                  : "bg-slate-50 border-transparent text-slate-300 cursor-not-allowed"
          }`}
        >

          <div className="flex items-center gap-3">

            <div
              className={`h-9 w-9 rounded-xl flex items-center justify-center font-black shrink-0 ${
                active
                  ? "bg-white/15 text-white"
                  : completed
                    ? "bg-emerald-100 text-emerald-600"
                    : "bg-slate-100 text-slate-400"
              }`}
            >

              {completed &&
              !active ? (
                <CheckCircle2 className="h-5 w-5" />
              ) : (
                step.id
              )}

            </div>

            <div>

              <p className="font-black">
                {step.label}
              </p>

              <p
                className={`text-[11px] mt-0.5 ${
                  active
                    ? "text-indigo-100"
                    : "text-slate-400"
                }`}
              >
                {step.description}
              </p>

            </div>

          </div>

        </button>

      );
    })}

  </div>

</div>



     {/* ADIM 1 — PLAN & HEDEFLER */}

{activeStep === 1 && (

<div>

  <div className="bg-white rounded-3xl border border-slate-200 shadow-sm p-6 md:p-8 space-y-7">
          <div className="flex items-center gap-3 pb-5 border-b border-slate-100">

            <div className="h-11 w-11 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
              <School className="h-5 w-5" />
            </div>

            <div>
              <h2 className="font-black text-lg text-slate-800">
                Plan Bilgileri
              </h2>

              <p className="text-xs text-slate-400 mt-0.5">
                Önce okul kademesini ve hedefleri seçin.
              </p>
            </div>

          </div>


          {/* OKUL KADEMESİ */}

          <div>

            <label className="block text-sm font-black text-slate-700 mb-2">
              Okul Kademesi
            </label>

            <select
              value={schoolLevel}
            onChange={e => {

  setSchoolLevel(
    e.target.value
  );

  setExcludedStandardCoreIds([]);

  resetActivityStep();
}}
              className="w-full px-4 py-3.5 bg-white border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
            >
              <option value="">
                Okul kademesi seçin
              </option>

              {SCHOOL_LEVELS.map(level => (
                <option
                  key={level.value}
                  value={level.value}
                >
                  {level.label}
                </option>
              ))}

            </select>

          </div>


{schoolLevel && (

  <div className="bg-indigo-50 border border-indigo-100 rounded-2xl p-4">

    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

      <div>

        <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
          Standart Plan Çalışmaları
        </p>

        <p className="font-black text-indigo-950 mt-1">
  {selectedSchoolLevelLabel} için{" "}
  {standardPlanRows.length} standart çalışma bulundu
</p>

<p className="text-xs font-black text-indigo-600 mt-1">
  {standardPlanIncludedCount}/{standardPlanRows.length} çalışma planda
</p>

       <p className="text-xs text-indigo-700 mt-2 leading-relaxed max-w-2xl">
  Rehber360 kademenize uygun standart çalışmaları
  plana otomatik ekler. Sabit çalışmalar korunur;
  diğerlerini isterseniz çıkarabilirsiniz.
  Faaliyet türü seçeneği bulunan çalışmalarda
  türü de değiştirebilirsiniz.
</p>

      </div>

      <button
        type="button"
        onClick={() =>
          setShowStandardActivities(
            prev => !prev
          )
        }
        className="shrink-0 px-4 py-2.5 bg-white border border-indigo-200 text-indigo-700 rounded-xl text-xs font-black hover:bg-indigo-50 transition"
      >
        {showStandardActivities
          ? "Listeyi Gizle"
          : `${standardPlanRows.length} Çalışmayı İncele`}
      </button>

    </div>


    {showStandardActivities && (

      <div className="mt-4 bg-white border border-indigo-100 rounded-2xl p-2 max-h-72 overflow-y-auto">

        {standardPlanRows.map(
  row => {

    const isIncluded =
      row.locked ||
      !excludedStandardCoreIds.includes(
        row.coreId
      );

        const audienceLabel =
      getStandardAudienceLabel(
        row.audience
      );

    const activityTypeOptions =
      getStandardActivityTypeOptions(
        row
      );

    const canEditActivityType =
      isIncluded &&
      activityTypeOptions.length > 0;

    return (

      <div
        key={row.id}
        className={`flex items-start justify-between gap-4 px-3 py-3 border-b border-slate-100 last:border-b-0 transition ${
          isIncluded
            ? "bg-white"
            : "bg-slate-50 opacity-60"
        }`}
      >

        <div className="min-w-0">

          <div className="flex items-center gap-2 flex-wrap">

            {row.locked ? (

              <Lock className="h-3.5 w-3.5 text-indigo-500 shrink-0" />

            ) : isIncluded ? (

              <CheckCircle2 className="h-3.5 w-3.5 text-emerald-500 shrink-0" />

            ) : (

              <X className="h-3.5 w-3.5 text-slate-400 shrink-0" />

            )}

            <div>

  <p
    className={`text-sm font-bold ${
      isIncluded
        ? "text-slate-800"
        : "text-slate-400 line-through"
    }`}
  >
    {row.contextLabel ||
      row.officialLabel}
  </p>

  {row.contextLabel && (

    <p className="text-[10px] text-slate-400 mt-0.5 font-semibold">
      Resmî faaliyet: {row.officialLabel}
    </p>

  )}

</div>

{audienceLabel && (

              <span className="text-[9px] font-black uppercase tracking-wider bg-slate-100 text-slate-500 px-2 py-0.5 rounded-md">
                {audienceLabel}
              </span>

            )}

          </div>

                    <p className="text-[11px] text-slate-400 mt-1 ml-5">
            {getStandardTimingLabel(
              row.timingRule
            )}
          </p>

          {activityTypeOptions.length > 0 && (

            <div className="mt-2 ml-5">

              <label className="block text-[9px] font-black uppercase tracking-wider text-slate-400 mb-1">
                Faaliyet Türü
                {row.activityLevel
                  ? ` · Düzey ${row.activityLevel}`
                  : ""}
              </label>

              <select
                value={
                  row.activityTypeId ||
                  ""
                }
                disabled={
                  !canEditActivityType
                }
                onChange={e =>
                  handleStandardActivityTypeChange(
                    row,
                    e.target.value
                  )
                }
                className="w-full max-w-md px-2.5 py-2 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 disabled:bg-slate-100 disabled:text-slate-400"
              >

                {activityTypeOptions.map(
                  type => (

                    <option
                      key={type.id}
                      value={type.id}
                    >
                      {type.shortLabel}
                      {type.id ===
                      row.defaultActivityTypeId
                        ? " · Sistem varsayılanı"
                        : ""}
                    </option>

                  )
                )}

              </select>

            </div>

          )}

        </div>


        {row.locked ? (

          <span className="text-[10px] font-black px-2.5 py-1 rounded-lg shrink-0 bg-indigo-50 text-indigo-700">
            Sabit
          </span>

        ) : (

        <button
  type="button"
  aria-pressed={isIncluded}
  onClick={() =>
    handleToggleStandardActivity(
      row
    )
  }
  className={`text-[10px] font-black px-3 py-1.5 rounded-lg border shrink-0 transition flex items-center gap-1.5 ${
    isIncluded
      ? "bg-white border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
      : "bg-white border-emerald-200 text-emerald-700 hover:bg-emerald-50"
  }`}
>
  {isIncluded ? (
    <>
      <X className="h-3 w-3" />
      Çıkart
    </>
  ) : (
    <>
      <CheckCircle2 className="h-3 w-3" />
      Geri Ekle
    </>
  )}
</button>

        )}

      </div>

    );
  }
)}


    <div className="flex flex-wrap gap-4 mt-3 text-[10px] font-semibold text-slate-500">

  <span>
    🔒 Sabit: planda korunur ve çıkarılamaz
  </span>

    <span>
    ✓ Varsayılan: başlangıçta eklenir, istenirse çıkarılabilir
  </span>

</div>

  </div>

)}

  </div>

)}

         {/* GENEL HEDEF */}

<div className="bg-slate-50 border border-slate-200 rounded-2xl px-4 py-3">

  <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

    <div className="flex items-center gap-3 min-w-0">

      <div className="h-9 w-9 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
        <Target className="h-4 w-4" />
      </div>

      <div className="min-w-0">

        <div className="flex items-center gap-2 flex-wrap">

          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
            Genel Hedef
          </p>

          <span className="flex items-center gap-1 text-[9px] font-black text-slate-400">
            <Lock className="h-3 w-3" />
            Sabit
          </span>

        </div>

        <p className="font-black text-slate-800 mt-1 leading-snug">
          {config.generalTarget.label}
        </p>

      </div>

    </div>


    <p className="text-[10px] text-slate-400 sm:text-right shrink-0">
      Sistem tarafından belirlenir
    </p>

  </div>

</div>


{/* SEÇİLECEK HEDEFLER */}

<div className="border-t border-slate-100 pt-6">

  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-2 mb-5">

    <div>

      <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
        Hedef Seçimleri
      </p>

      <h3 className="font-black text-slate-800 mt-1">
        1 Yerel + 2 Özel Hedef
      </h3>

      <p className="text-xs text-slate-400 mt-1">
        Yıllık planınız için kalan üç hedefi seçin.
      </p>

    </div>


    <div
      className={`text-xs font-black px-3 py-1.5 rounded-full ${
        selectedEditableTargetCount === 3 &&
        !specialTargetsDuplicate
          ? "bg-emerald-50 text-emerald-700"
          : "bg-slate-100 text-slate-500"
      }`}
    >
      {selectedEditableTargetCount}/3 seçildi
    </div>

  </div>


  <div className="bg-indigo-50/60 border border-indigo-100 rounded-2xl p-4">

    <TargetSelect
      label="Yerel Hedef"
      value={localTargetId}
      onChange={value => {
        setLocalTargetId(value);
        resetActivityStep();
      }}
      helpText="İliniz/ilçeniz için bildirilen hedefi seçin."
    />

  </div>


  <div className="mt-4">

    <div className="flex items-center gap-2 mb-3">

      <p className="text-xs font-black text-slate-700">
        Özel Hedefler
      </p>

      <span className="text-[10px] text-slate-400">
        Birbirinden farklı iki hedef seçin
      </span>

    </div>


    <div className="grid md:grid-cols-2 gap-4">

      <div className="bg-white border border-slate-200 rounded-2xl p-4">

        <TargetSelect
          label="Özel Hedef 1"
          value={specialTarget1Id}
          onChange={value => {
            setSpecialTarget1Id(value);
            resetActivityStep();
          }}
        />

      </div>


      <div className="bg-white border border-slate-200 rounded-2xl p-4">

        <TargetSelect
          label="Özel Hedef 2"
          value={specialTarget2Id}
          onChange={value => {
            setSpecialTarget2Id(value);
            resetActivityStep();
          }}
        />

      </div>

    </div>

  </div>


  {specialTargetsDuplicate && (

    <div className="mt-4 bg-amber-50 border border-amber-200 text-amber-800 rounded-2xl p-4 text-sm font-semibold flex items-start gap-2">

      <span className="shrink-0">
        ⚠
      </span>

      <span>
        Özel Hedef 1 ve Özel Hedef 2 birbirinden farklı olmalıdır.
      </span>

    </div>

  )}

</div>


{/* DEVAM AKSİYONU */}

<div className="border-t border-slate-100 pt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">

  <div>

    {canContinue ? (

      <div className="flex items-center gap-2 text-emerald-700">

        <CheckCircle2 className="h-4 w-4" />

        <div>

          <p className="text-sm font-black">
            Hedef yapısı hazır
          </p>

          <p className="text-[10px] text-emerald-600 mt-0.5">
            Genel, yerel ve özel hedefler tamamlandı.
          </p>

        </div>

      </div>

    ) : (

      <div>

        <p className="text-xs font-black text-slate-600">
          Hedef seçimlerini tamamlayın
        </p>

        <p className="text-[10px] text-slate-400 mt-1">
          Devam etmek için yerel hedef ve iki farklı özel hedef seçilmelidir.
        </p>

      </div>

    )}

  </div>


  <button
    type="button"
    onClick={handleContinue}
    disabled={!canContinue}
    className="w-full md:w-auto px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-2xl font-black transition flex items-center justify-center gap-2 shadow-sm disabled:shadow-none"
  >
    Hedefleri Onayla
    <ChevronRight className="h-4 w-4" />
  </button>
           </div>
           </div>
           </div>

)}

{activeStep === 2 &&
 showSummary && (

  <div className="bg-slate-50 border border-slate-200 rounded-3xl p-5 md:p-8 space-y-6">

    {/* ADIM 2 BAŞLIK */}

    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

      <div>

        <div className="flex items-center gap-2 text-indigo-600 text-xs font-black uppercase tracking-widest">

          <ClipboardList className="h-4 w-4" />

          Adım 2

        </div>

        <h2 className="text-2xl font-black text-slate-900 mt-2">

  {planningMode === "auto"
    ? "Otomatik Plan Robotu"
    : "Hedef Faaliyetlerini Seç"}

</h2>


<p className="text-sm text-slate-500 mt-2 max-w-3xl leading-relaxed">

 {planningMode === "auto"
  ? (
      <>
        Hedefleriniz hazır. Rehber360 faaliyetleri
        ve yıllık takvimi sizin için otomatik oluşturacak.
      </>
    )
    : (
        <>
          Her hedef için yalnızca resmî RPD faaliyet
          havuzundaki çalışmaları seçin. Düzey 1, 2 ve 3
          faaliyetleri kullanılabilir; zorunlu minimumlar
          Düzey 1 faaliyetleri üzerinden kontrol edilir.
        </>
      )}

</p>

      </div>


{planningMode === "manual" && (

  <div className="bg-white border border-slate-200 rounded-2xl px-4 py-3">

    <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
      Kural
    </p>

    <p className="text-xs font-bold text-slate-700 mt-1">
      Öğrenci 2 • Veli 2 • Öğretmen 1 önerilen
    </p>

  </div>

)}

    </div>





   {/* OTOMATİK PLAN ROBOTU */}

{planningMode === "auto" && (

  <div className="bg-white border border-indigo-200 rounded-3xl overflow-hidden shadow-sm">

    <div className="p-5 md:p-7 bg-gradient-to-br from-indigo-50 to-white">

     <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">

  <div>

    <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
      Plan Özeti
    </p>

    <h3 className="font-black text-lg text-slate-900 mt-1">
      {selectedTargets.length} hedef planlamaya hazır
    </h3>

  </div>


  <div className="flex items-center gap-2 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-xl px-3 py-2">

    <CheckCircle2 className="h-4 w-4 shrink-0" />

    <span className="text-xs font-black">
      {standardPlanIncludedCount} standart çalışma dahil
    </span>

  </div>

</div>


      {/* SEÇİLEN HEDEFLER */}

      <div className="mt-6">

        <p className="text-[10px] font-black uppercase tracking-widest text-slate-400">
          Planın Hedefleri
        </p>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3 mt-3">

          {selectedTargets.map(target => (

            <div
              key={target.type}
              className="bg-white border border-slate-200 rounded-2xl px-4 py-3"
            >

              <p className="text-[9px] font-black uppercase tracking-wider text-indigo-500">
                {target.shortLabel}
              </p>

              <p className="text-xs font-black text-slate-800 mt-1 leading-snug">
                {getPlanningTargetDisplayLabel(
  target.label
)}
              </p>

            </div>

          ))}

        </div>

      </div>


      {/* ROBOTUN YAPACAĞI İŞLER */}

      <div className="grid md:grid-cols-3 gap-3 mt-6">

        <div className="bg-white border border-slate-200 rounded-2xl p-4">

          <div className="flex items-center gap-2 text-indigo-600">

            <CheckCircle2 className="h-4 w-4" />

            <p className="text-xs font-black">
              Resmî faaliyetler
            </p>

          </div>

          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
            Hedeflere uygun doğrulanmış faaliyetler otomatik seçilir.
          </p>

        </div>


        <div className="bg-white border border-slate-200 rounded-2xl p-4">

          <div className="flex items-center gap-2 text-indigo-600">

            <ClipboardList className="h-4 w-4" />

            <p className="text-xs font-black">
              Standart çalışmalar
            </p>

          </div>

          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
            {standardPlanIncludedCount} standart çalışma plana dahil edilir.
          </p>

        </div>


        <div className="bg-white border border-slate-200 rounded-2xl p-4">

          <div className="flex items-center gap-2 text-indigo-600">

            <CalendarDays className="h-4 w-4" />

            <p className="text-xs font-black">
              Yıllık takvim
            </p>

          </div>

          <p className="text-[11px] text-slate-500 mt-2 leading-relaxed">
            Faaliyetler uygun haftalara otomatik olarak dağıtılır.
          </p>

        </div>

      </div>

    </div>


    {/* ANA CTA */}

    <div className="px-5 md:px-7 py-5 border-t border-slate-100 bg-slate-50 flex flex-col md:flex-row md:items-center md:justify-between gap-4">

      <div>

        <p className="text-sm font-black text-slate-800">
          Planınızı oluşturmaya hazırsınız
        </p>

        <p className="text-[11px] text-slate-500 mt-1">
          Oluşturulan planı sonraki ekranda inceleyebilir,
          tarih ve sınıf kapsamlarını düzenleyebilirsiniz.
        </p>

      </div>


      <button
        type="button"
        onClick={
          handleGenerateAutoPlan
        }
        disabled={
          !canContinue
        }
        className="shrink-0 px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-sm font-black transition shadow-sm disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2"
      >

        Planı Otomatik Oluştur

        <ChevronRight className="h-4 w-4" />

      </button>

    </div>

  </div>

)}

    {planningMode === "manual" && (
  <>

    {/* HEDEF SEKMELERİ */}

    <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">

      {selectedTargets.map(target => {

        const active =
          activeTargetType ===
          target.type;


        return (

          <button

            type="button"

            key={target.type}

            onClick={() =>
              setActiveTargetType(
                target.type
              )
            }

            className={`text-left rounded-2xl border p-4 transition ${
              active
                ? "bg-indigo-600 border-indigo-600 text-white shadow-lg shadow-indigo-100"
                : "bg-white border-slate-200 hover:border-indigo-200"
            }`}

          >

            <p
              className={`text-[10px] font-black uppercase tracking-widest ${
                active
                  ? "text-indigo-200"
                  : "text-slate-400"
              }`}
            >
              {target.shortLabel}
            </p>


            <p
              className={`font-black mt-1 leading-snug ${
                active
                  ? "text-white"
                  : "text-slate-800"
              }`}
            >
              {getPlanningTargetDisplayLabel(
  target.label
)}
            </p>


            {target.developmentArea && (

              <p
                className={`text-[10px] mt-2 font-semibold ${
                  active
                    ? "text-indigo-100"
                    : "text-slate-400"
                }`}
              >
                {
                  DEVELOPMENT_AREA_LABELS[
                    target.developmentArea
                  ]
                }
              </p>

            )}

          </button>

        );
      })}

    </div>


    {/* AKTİF HEDEF */}

    {selectedTargets
      .filter(
        target =>
          target.type ===
          activeTargetType
      )
      .map(target => (

        <div
          key={target.type}
          className="space-y-5"
        >

          <div className="bg-indigo-50 border border-indigo-100 rounded-2xl px-5 py-4">

            <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
              Şu an düzenleniyor
            </p>

            <p className="font-black text-indigo-950 mt-1">
              {getPlanningTargetDisplayLabel(
  target.label
)}
            </p>


            {target.developmentArea ? (

              <p className="text-xs text-indigo-600 mt-1">

                Öğrenci faaliyetleri bu hedefin
                {" "}
                <strong>
                  {
                    DEVELOPMENT_AREA_LABELS[
                      target.developmentArea
                    ]
                  }
                </strong>
                {" "}
                alanındaki resmî faaliyetlerden gösterilir.

              </p>

            ) : (

              <p className="text-xs text-indigo-600 mt-1">

                Genel hedef EK-2 gelişim alanına bağlı olmadığı için
                öğrenci tarafında resmî faaliyet havuzunun tamamı gösterilir.

              </p>

            )}

          </div>


          <div className="grid xl:grid-cols-3 gap-4">

            <AudienceActivityCard
              target={target}
              audience="student"
            />

            <AudienceActivityCard
              target={target}
              audience="parent"
            />

            <AudienceActivityCard
              target={target}
              audience="teacher"
            />

          </div>

        </div>

      ))}

    {/* EK RESMÎ GRUP ÇALIŞMALARI */}

<div className="border-t border-slate-200 pt-7 space-y-5">

  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">

    <div>

      <p className="text-[10px] font-black uppercase tracking-widest text-violet-500">
        İsteğe Bağlı
      </p>

      <h3 className="text-xl font-black text-slate-900 mt-1">
        Ek Resmî Grup Çalışmaları
      </h3>

      <p className="text-sm text-slate-500 mt-2 max-w-3xl leading-relaxed">
        Bireyi tanıma, toplantılar, program yönetimi,
        psikososyal müdahale, işbirliği, grupla psikolojik
        danışma ve mesleki gelişim gibi resmî grup
        çalışmalarını planınıza ekleyebilirsiniz.
      </p>

    </div>

    <div className="bg-violet-50 border border-violet-100 rounded-2xl px-4 py-3">

      <p className="text-[10px] font-black uppercase tracking-widest text-violet-500">
        Seçilen
      </p>

      <p className="text-lg font-black text-violet-800 mt-1">
        {selectedAdditionalActivities.length}
      </p>

    </div>

  </div>


  <div className="grid md:grid-cols-3 gap-4">

    {/* KATEGORİ */}

    <div>

      <label className="block text-xs font-black text-slate-600 mb-2">
        Faaliyet Kategorisi
      </label>

      <select
        value={additionalCategory}
        onChange={e => {
          setAdditionalCategory(
            e.target.value
          );

          setAdditionalSearch("");
        }}
        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-violet-500"
      >

        <option value="">
          Kategori seçin
        </option>

        {additionalCategories.map(
          category => (

            <option
              key={category.id}
              value={category.id}
            >
              {category.label}
            </option>

          )
        )}

      </select>

    </div>


    {/* ARAMA */}

    <div>

      <label className="block text-xs font-black text-slate-600 mb-2">
        Faaliyet Ara
      </label>

      <input
        type="text"
        value={additionalSearch}
        onChange={e =>
          setAdditionalSearch(
            e.target.value
          )
        }
        placeholder="Örn. sosyometri, toplantı..."
        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-violet-500"
      />

    </div>


    {/* FAALİYET */}

    <div>

      <label className="block text-xs font-black text-slate-600 mb-2">
        Resmî Faaliyet
      </label>

      <select
        value=""
        disabled={
          !additionalCategory
        }
        onChange={e =>
          handleAddAdditionalActivity(
            e.target.value
          )
        }
        className="w-full px-4 py-3 bg-white border border-slate-200 rounded-2xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-violet-500 disabled:bg-slate-100 disabled:text-slate-400"
      >

        <option value="">
          + Faaliyet ekle
        </option>

        {additionalActivityOptions.map(
          activity => {

            const mebbisCode =
              getAdditionalMebbisCode(
                activity
              );

            return (

              <option
                key={activity.id}
                value={activity.id}
              >
                {mebbisCode
                  ? `[${mebbisCode}] `
                  : ""}
                {activity.officialLabel}
              </option>

            );
          }
        )}

      </select>

    </div>

  </div>


  {/* SEÇİLEN EK FAALİYETLER */}

  {selectedAdditionalActivities.length > 0 && (

    <div className="grid md:grid-cols-2 gap-3">

      {selectedAdditionalActivities.map(
        activityId => {

          const activity =
            getAdditionalGroupActivityById(
              activityId
            );

          if (!activity) {
            return null;
          }

          const mebbisCode =
            getAdditionalMebbisCode(
              activity
            );

           const defaultActivityType =
  getDefaultActivityTypeForAdditionalCategory(
    activity.category
  );

          return (

            <div
              key={activity.id}
              className="bg-white border border-slate-200 rounded-2xl p-4 flex items-start justify-between gap-3 shadow-sm"
            >

              <div>

                <div className="flex items-center gap-2 flex-wrap">

                  {mebbisCode && (

                    <span className="text-[10px] font-black bg-violet-100 text-violet-700 px-2 py-0.5 rounded-md">
                      {mebbisCode}
                    </span>

                  )}

                  <span className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                    {activity.categoryLabel}
                  </span>

                </div>

                <p className="text-sm font-black text-slate-800 mt-2 leading-snug">
  {activity.officialLabel}
</p>

{defaultActivityType ? (

  <div className="mt-2">

    <p className="text-[10px] text-emerald-700 font-bold">
      Faaliyet Türü: {defaultActivityType.shortLabel}
    </p>

    <p className="text-[10px] text-emerald-600 mt-1 font-medium">
      Resmî kaynakla doğrulanmış eşleme
    </p>

  </div>

) : (

  <div className="mt-3 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2.5">

    <p className="text-[10px] font-black uppercase tracking-wider text-amber-700">
      Faaliyet türü doğrulanmadı
    </p>

    <p className="text-[10px] text-amber-700 mt-1 leading-relaxed font-medium">
      Bu çalışma için MEBBİS'te kullanılacak faaliyet türü
      kaynaklarımızda henüz kesin olarak eşleştirilmedi.
      Yanlış tür üretmemek için bu faaliyet şu an plana
      dahil edilemez.
    </p>

  </div>

)}
      className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-violet-500"
    >

      <option value="">
        Faaliyet türü seçilmedi
      </option>

     

{!mebbisCode && (

  <p className="text-[10px] text-amber-600 mt-1 font-semibold">
    Kodlu kaynakta kesin MEBBİS eşleşmesi bulunamadı.
  </p>

)}

              </div>

              <button
                type="button"
                onClick={() =>
                  handleRemoveAdditionalActivity(
                    activity.id
                  )
                }
                className="h-8 w-8 rounded-xl bg-slate-50 border border-slate-200 text-slate-400 hover:text-red-500 hover:border-red-200 flex items-center justify-center shrink-0 transition"
                title="Faaliyeti kaldır"
              >

                <X className="h-4 w-4" />

              </button>

            </div>

          );
        }
      )}

    </div>

  )}

</div>


    {/* DEVAM */}

    <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 pt-2">

      <div>

        {canConfirmActivities ? (

          <div className="flex items-center gap-2 text-emerald-600 font-black text-sm">

            <CheckCircle2 className="h-5 w-5" />

            Zorunlu faaliyet seçimleri tamamlandı

          </div>

        ) : (

        <p className="text-sm text-slate-500 font-semibold">
  {!requiredActivitySelectionsComplete
    ? "Devam etmek için zorunlu Düzey 1 faaliyetlerini tamamlayın."
    : !targetActivityDetailsComplete
      ? "Seçtiğiniz hedef faaliyetlerinin düzey ve faaliyet türlerini tamamlayın."
      : !additionalActivityDetailsComplete
        ? "Ek çalışmalardan en az birinin faaliyet türü henüz doğrulanmadı. Bu çalışmayı kaldırın veya doğrulanmış bir çalışma seçin."
        : "Faaliyet seçimlerini tamamlayın."}
</p>

        )}

      </div>


      <button

        type="button"

       disabled={
  !canConfirmActivities
}

        onClick={
  handleConfirmActivities
}

        className="px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 disabled:bg-slate-200 disabled:text-slate-400 text-white rounded-2xl font-black transition flex items-center justify-center gap-2"

      >

        Faaliyetleri Onayla

        <ChevronRight className="h-4 w-4" />

      </button>

    </div>

  </>
)}

{planGenerationErrors.length > 0 && (

  <div className="bg-rose-50 border border-rose-200 rounded-2xl p-4">

    <p className="font-black text-rose-800">
      Plan oluşturulamadı
    </p>

    <div className="mt-2 space-y-1">

      {planGenerationErrors.map(
        (error, index) => (

          <p
            key={
              error.code ||
              index
            }
            className="text-xs text-rose-700 font-semibold"
          >
            {error.message}
          </p>

        )
      )}

    </div>

  </div>

)}




   </div>

)}


{/* ADIM 3 — SINIF & ŞUBELER */}

{activeStep === 3 &&
 activitiesConfirmed &&
 generatedAnnualPlan && (

  <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-sm space-y-6">

   
    {/* SINIF / ŞUBE YAPISI */}

<div className="bg-white border border-slate-200 rounded-3xl p-5 md:p-6">

  <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-6">

    <div className="flex-1">

      <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
        Plan Kapsamı
      </p>

      <h3 className="font-black text-slate-900 text-lg mt-1">
        Sınıf / Şube Yapısı
      </h3>

      <p className="text-xs text-slate-500 mt-2 leading-relaxed max-w-2xl">
        Planınızda kullanılacak sınıfları
        Sınıf Yönetimi'nden getirebilir veya
        birkaç tıklamayla hızlıca oluşturabilirsiniz.
      </p>


      {/* ANA SEÇENEKLER */}

      <div className="grid md:grid-cols-2 gap-3 mt-5">

        <button
          type="button"
          onClick={() =>
            setClassSetupMode(
              previous =>
                previous === "managed"
                  ? ""
                  : "managed"
            )
          }
          className={`text-left rounded-2xl border p-4 transition ${
            classSetupMode === "managed"
              ? "bg-indigo-50 border-indigo-300"
              : "bg-slate-50 border-slate-200 hover:border-indigo-200"
          }`}
        >

          <div className="flex items-start gap-3">

            <div className="h-10 w-10 rounded-xl bg-indigo-100 text-indigo-600 flex items-center justify-center shrink-0">
              <School className="h-5 w-5" />
            </div>

            <div>

              <p className="font-black text-slate-800">
                Sınıf Yönetimi'nden Getir
              </p>

              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                Daha önce Rehber360'a yüklediğiniz
                sınıfları doğrudan kullanın.
              </p>

              {managedClassLabels.length > 0 && (

                <p className="text-[10px] font-black text-indigo-600 mt-2">
                  {managedClassLabels.length} kayıtlı sınıf bulundu
                </p>

              )}

            </div>

          </div>

        </button>


        <button
          type="button"
          onClick={() =>
            setClassSetupMode(
              previous =>
                previous === "quick"
                  ? ""
                  : "quick"
            )
          }
          className={`text-left rounded-2xl border p-4 transition ${
            classSetupMode === "quick"
              ? "bg-violet-50 border-violet-300"
              : "bg-slate-50 border-slate-200 hover:border-violet-200"
          }`}
        >

          <div className="flex items-start gap-3">

            <div className="h-10 w-10 rounded-xl bg-violet-100 text-violet-600 flex items-center justify-center shrink-0">
              <GraduationCap className="h-5 w-5" />
            </div>

            <div>

              <p className="font-black text-slate-800">
                Hızlı Sınıf / Şube Ekle
              </p>

              <p className="text-[11px] text-slate-500 mt-1 leading-relaxed">
                Sınıf düzeyini ve şubeleri seçin;
                liste otomatik oluşsun.
              </p>

            </div>

          </div>

        </button>

      </div>


      {/* SINIF YÖNETİMİ PANELİ */}

      {classSetupMode === "managed" && (

        <div className="mt-4 bg-indigo-50 border border-indigo-200 rounded-2xl p-4">

          {managedClassLabels.length > 0 ? (

            <>

              <div className="flex items-center justify-between gap-3">

                <div>

                  <p className="text-xs font-black text-indigo-900">
                    Sınıf Yönetimi'ndeki sınıflar
                  </p>

                  <p className="text-[10px] text-indigo-600 mt-1">
                    Kullanılacak sınıfları kontrol edin.
                  </p>

                </div>

                <span className="text-[10px] font-black bg-white border border-indigo-200 text-indigo-700 px-2.5 py-1 rounded-full">
                  {managedClassLabels.length} sınıf
                </span>

              </div>


              <div className="flex flex-wrap gap-2 mt-4">

                {managedClassLabels.map(
                  label => (

                    <span
                      key={label}
                      className="px-2.5 py-1.5 bg-white border border-indigo-200 rounded-lg text-xs font-black text-indigo-800"
                    >
                      {label}
                    </span>

                  )
                )}

              </div>


              <button
                type="button"
                onClick={
                  handleUseManagedClasses
                }
                className="mt-4 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-black transition"
              >
                Bu Sınıfları Kullan
              </button>

            </>

          ) : (

            <div>

              <p className="text-xs font-black text-amber-800">
                Sınıf Yönetimi'nde kayıtlı sınıf bulunamadı
              </p>

              <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
                Öğrenci listelerinizi Sınıf Yönetimi'ne
                yükleyebilir veya hızlı sınıf ekleme
                seçeneğini kullanabilirsiniz.
              </p>


              {typeof onOpenClassManager ===
                "function" && (

                <button
                  type="button"
                  onClick={
                    onOpenClassManager
                  }
                  className="mt-3 px-4 py-2.5 bg-white border border-amber-300 text-amber-800 rounded-xl text-xs font-black hover:bg-amber-100 transition"
                >
                  Sınıf Yönetimi'ne Git
                </button>

              )}

            </div>

          )}

        </div>

      )}


      {/* HIZLI SINIF OLUŞTURUCU */}

      {classSetupMode === "quick" && (

        <div className="mt-4 bg-violet-50 border border-violet-200 rounded-2xl p-4">

          {!schoolLevel ? (

            <p className="text-xs font-black text-amber-700">
              Önce okul kademesini seçin.
            </p>

          ) : (

            <>

              <div>

                <p className="text-xs font-black text-violet-900">
                  Sınıf ve şubeleri seçin
                </p>

                <p className="text-[10px] text-violet-600 mt-1">
                  Birden fazla şubeyi aynı anda işaretleyebilirsiniz.
                </p>

              </div>


              <div className="space-y-3 mt-4">

                {quickGradeOptions.map(
                  gradeOption => (

                    <div
                      key={gradeOption.key}
                      className="bg-white border border-violet-100 rounded-xl p-3"
                    >

                      <p className="text-xs font-black text-slate-700 mb-2">
                        {gradeOption.label}
                      </p>


                      <div className="flex flex-wrap gap-2">

                        {QUICK_CLASS_BRANCHES.map(
                          branch => {

                            const selected =
                              (
                                quickClassSelections[
                                  gradeOption.key
                                ] || []
                              ).includes(
                                branch
                              );


                            return (

                              <button
                                type="button"
                                key={
                                  `${gradeOption.key}_${branch}`
                                }
                                onClick={() =>
                                  handleToggleQuickBranch(
                                    gradeOption.key,
                                    branch
                                  )
                                }
                                className={`h-9 min-w-9 px-3 rounded-lg border text-xs font-black transition ${
                                  selected
                                    ? "bg-violet-600 border-violet-600 text-white"
                                    : "bg-white border-slate-200 text-slate-600 hover:border-violet-300"
                                }`}
                              >
                                {branch}
                              </button>

                            );
                          }
                        )}

                      </div>

                    </div>

                  )
                )}

              </div>


              <div className="flex flex-wrap items-center gap-3 mt-4">

                <button
                  type="button"
                  onClick={
                    handleApplyQuickClasses
                  }
                  disabled={
                    quickClassLabels.length === 0
                  }
                  className="px-4 py-2.5 bg-violet-600 hover:bg-violet-700 text-white rounded-xl text-xs font-black transition disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Seçilen Sınıfları Kullan
                </button>


                {quickClassLabels.length > 0 && (

                  <span className="text-xs font-black text-violet-700">
                    {quickClassLabels.length} sınıf/şube seçildi
                  </span>

                )}

              </div>

            </>

          )}

        </div>

      )}


      {/* GELİŞMİŞ METİN GİRİŞİ */}

      <details className="mt-4 bg-slate-50 border border-slate-200 rounded-2xl">

        <summary className="cursor-pointer px-4 py-3 text-xs font-black text-slate-600 select-none">
          Gelişmiş / Metinle Düzenle
        </summary>

        <div className="px-4 pb-4 border-t border-slate-200">

          <p className="text-[10px] text-slate-500 mt-3 leading-relaxed">
            Özel sınıf adları kullanıyorsanız sınıf ve
            şubeleri virgül, noktalı virgül veya alt satırla
            yazabilirsiniz.
          </p>


          <textarea
            value={
              classStructureText
            }
            onChange={e =>
              setClassStructureText(
                e.target.value
              )
            }
            rows={3}
            placeholder={
              "Örn. 9/A, 9/B, 10/C, 11/AMP-A, 12/B"
            }
            className="w-full mt-3 px-4 py-3 bg-white border border-slate-200 rounded-xl text-sm font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500 resize-y"
          />


          <button
            type="button"
            onClick={
              handleApplyClassStructure
            }
            className="mt-3 px-4 py-2.5 bg-slate-800 hover:bg-slate-900 text-white rounded-xl text-xs font-black transition"
          >
            Metinle Girilen Sınıfları Kullan
          </button>

        </div>

      </details>

    </div>


    {/* MEVCUT PLAN SINIFLARI */}

    <div className="lg:w-[360px]">

      {planClassOptions.length === 0 ? (

        <div className="bg-amber-50 border border-amber-200 rounded-2xl p-4">

          <p className="text-xs font-black text-amber-800">
            Sınıf/şube yapısı henüz tanımlanmadı
          </p>

          <p className="text-[11px] text-amber-700 mt-1 leading-relaxed">
            Plan oluşturulabilir; ancak Excel çıktısından önce
            sınıf/şube kapsamının tamamlanması önerilir.
          </p>

        </div>

      ) : (

        <div className="bg-emerald-50 border border-emerald-200 rounded-2xl p-4">

          <div className="flex items-center justify-between gap-2">

            <p className="text-[10px] font-black uppercase tracking-widest text-emerald-600">
              Planda Kullanılan Sınıflar
            </p>

            <span className="text-[10px] font-black text-emerald-700">
              {planClassOptions.length}
            </span>

          </div>


          <div className="flex flex-wrap gap-2 mt-3">

            {planClassOptions.map(
              classItem => (

                <span
                  key={classItem.id}
                  className="px-2.5 py-1 bg-white border border-emerald-200 rounded-lg text-xs font-black text-emerald-800"
                >
                  {classItem.label}
                </span>

              )
            )}

          </div>

        </div>

      )}

        </div>

  </div>

</div>


{/* ADIM 3 DEVAM */}

<div className="border-t border-slate-100 pt-5 flex flex-col md:flex-row md:items-center md:justify-between gap-4">

  <div>

    {planClassOptions.length > 0 ? (

      <div className="flex items-center gap-2 text-emerald-700">

        <CheckCircle2 className="h-4 w-4 shrink-0" />

        <div>
          <p className="text-sm font-black">
            Sınıf yapısı hazır
          </p>

          <p className="text-[10px] text-emerald-600 mt-0.5">
            {planClassOptions.length} sınıf/şube plan kapsamına eklendi.
          </p>
        </div>

      </div>

    ) : (

      <div>

        <p className="text-xs font-black text-amber-700">
          Sınıf/şube tanımlamak isteğe bağlıdır
        </p>

        <p className="text-[10px] text-slate-500 mt-1">
          Genel planı sınıf tanımlamadan da önizleyebilirsiniz.
        </p>

      </div>

    )}

  </div>


  <button
    type="button"
    onClick={() =>
      setActiveStep(4)
    }
    className="w-full md:w-auto px-6 py-3.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-2xl text-sm font-black transition shadow-sm flex items-center justify-center gap-2"
  >
    Plan Önizlemeye Geç
    <ChevronRight className="h-4 w-4" />
  </button>

</div>

  </div>

)}


{/* ADIM 4 — PLAN ÖNİZLEME */}

{activeStep === 4 &&
 activitiesConfirmed &&
 generatedAnnualPlan && (

  <div className="bg-white border border-slate-200 rounded-3xl p-6 md:p-8 shadow-sm space-y-6">


{/* TAKVİMLİ PLAN LİSTESİ */}

    <div className="border border-slate-200 rounded-2xl overflow-hidden">

      <div className="bg-slate-50 border-b border-slate-200 px-5 py-4 flex flex-col md:flex-row md:items-center md:justify-between gap-2">

        <div>

          <p className="text-[10px] font-black uppercase tracking-widest text-indigo-500">
            Yıllık Takvim
          </p>

          <h3 className="font-black text-slate-800 mt-1">
            Takvime Yerleştirilen Çalışmalar
          </h3>

        </div>

      <div className="flex flex-col sm:flex-row sm:items-center gap-3">

  <p className="text-xs font-bold text-slate-500 whitespace-nowrap">
    {
      (generatedAnnualPlan.activities || [])
        .filter(row => row.startDate)
        .length
    }
    /
    {
      (generatedAnnualPlan.activities || [])
        .length
    }
    {" "}faaliyet tarihlendirildi
  </p>


  <div className="flex flex-wrap items-center gap-2">

    <button
      type="button"
      onClick={() =>
        setActiveStep(3)
      }
      className="px-3.5 py-2 bg-white border border-slate-200 text-slate-600 rounded-xl text-[11px] font-black hover:bg-slate-100 transition"
    >
      ← Sınıf & Şubelere Dön
    </button>


    <button
      type="button"
      onClick={
        handleExportAnnualPlanExcel
      }
      className="px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-xl text-[11px] font-black transition shadow-sm"
    >
      Excel'e Aktar
    </button>

  </div>

</div>

      </div>


      <div className="overflow-x-auto">

        <table className="w-full text-left">

          <thead className="bg-white border-b border-slate-200">

           <tr>

  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-slate-400">
    Ay
  </th>

  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-slate-400">
    Yapılacak Çalışma
  </th>

  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-slate-400">
    Tarih
  </th>

  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-slate-400">
    Hedef
  </th>

  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-slate-400">
    Açıklama / Faaliyet Türü
  </th>

  <th className="px-4 py-3 text-[10px] font-black uppercase tracking-wider text-slate-400">
    Sınıf / Şube
  </th>

</tr>

          </thead>


          <tbody className="divide-y divide-slate-100">

            {[...(generatedAnnualPlan.activities || [])]

              .sort(
                (a, b) =>
                  (
                    a.startDate ||
                    "9999-12-31"
                  ).localeCompare(
                    b.startDate ||
                    "9999-12-31"
                  )
              )

              .map(
                (row, index) => {

                  const monthLabels = {
                    1: "Ocak",
                    2: "Şubat",
                    3: "Mart",
                    4: "Nisan",
                    5: "Mayıs",
                    6: "Haziran",
                    7: "Temmuz",
                    8: "Ağustos",
                    9: "Eylül",
                    10: "Ekim",
                    11: "Kasım",
                    12: "Aralık"
                  };

                  const audienceLabels = {
                    student: "Öğrenci",
                    parent: "Veli",
                    teacher: "Öğretmen"
                  };


const getTargetDisplayLabel =
  row => {

    if (
      !row?.targetType ||
      !row?.targetLabel
    ) {
      return "HEDEF DIŞI";
    }

    const displayTargetLabel =
      getPlanningTargetDisplayLabel(
        row.targetLabel
      );

    if (
      row.targetType ===
      "general"
    ) {
      return (
        `GENEL HEDEF - ${displayTargetLabel}`
      );
    }

    if (
      row.targetType ===
      "local"
    ) {
      return (
        `YEREL HEDEF - ${displayTargetLabel}`
      );
    }

    if (
      row.targetType ===
        "special1" ||
      row.targetType ===
        "special2"
    ) {
      return (
        `ÖZEL HEDEF - ${displayTargetLabel}`
      );
    }

    return displayTargetLabel;
  };

const getActivityTypeDisplayLabel =
  row => {

    if (
      row?.activityTypeLabel
    ) {

      return row.activityLevel
        ? `${row.activityTypeLabel} (Düzey ${row.activityLevel})`
        : row.activityTypeLabel;
    }

    return null;
  };



                  const formatDate =
                    value => {

                      if (!value) {
                        return null;
                      }

                      return value
                        .split("-")
                        .reverse()
                        .join(".");
                    };


                  return (

                    <tr
                      key={
                        row.id ||
                        `${row.activityId}_${index}`
                      }
                      className="hover:bg-slate-50"
                    >

                      <td className="px-4 py-3 align-top">

                        <span className="text-xs font-black text-indigo-600">
                          {monthLabels[row.month] || "—"}
                        </span>

                      </td>


                     <td className="px-4 py-3 align-top">

  <div className="flex items-start gap-2">

    {row.mebbisCode && (

      <span className="text-[10px] font-black bg-indigo-50 border border-indigo-100 text-indigo-700 px-2 py-0.5 rounded-md shrink-0 mt-0.5">
        {row.mebbisCode}
      </span>

    )}

    <p className="text-sm font-bold text-slate-800">
      {row.contextLabel ||
        row.officialLabel}
    </p>

  </div>

  {row.contextLabel && (

                          <p className="text-[10px] text-slate-400 mt-1">
                            Resmî faaliyet: {row.officialLabel}
                          </p>

                        )}

                        {row.targetLabel && (

  <p className="text-[10px] text-violet-600 font-semibold mt-1">
    {getPlanningTargetDisplayLabel(
      row.targetLabel
    )}
  </p>

)}

                      </td>


                                           <td className="px-4 py-3 align-top min-w-[190px]">

                        {editingDateRowId ===
                        row.id ? (

                          <div className="space-y-2">

                            <div>

                              <label className="block text-[9px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                Başlangıç
                              </label>

                              <input
                                type="date"
                                min={
                                  SCHOOL_CALENDAR_2026_2027
                                    .firstTerm.start
                                }
                                max={
                                  SCHOOL_CALENDAR_2026_2027
                                    .secondTerm.end
                                }
                                value={
                                  dateEditDraft.startDate
                                }
                                onChange={e =>
                                  setDateEditDraft(
                                    prev => ({
                                      ...prev,
                                      startDate:
                                        e.target.value
                                    })
                                  )
                                }
                                className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                              />

                            </div>


                            <div>

                              <label className="block text-[9px] font-black uppercase tracking-wider text-slate-400 mb-1">
                                Bitiş
                              </label>

                              <input
                                type="date"
                                min={
                                  dateEditDraft.startDate ||
                                  SCHOOL_CALENDAR_2026_2027
                                    .firstTerm.start
                                }
                                max={
                                  SCHOOL_CALENDAR_2026_2027
                                    .secondTerm.end
                                }
                                value={
                                  dateEditDraft.endDate
                                }
                                onChange={e =>
                                  setDateEditDraft(
                                    prev => ({
                                      ...prev,
                                      endDate:
                                        e.target.value
                                    })
                                  )
                                }
                                className="w-full px-2 py-1.5 bg-white border border-slate-200 rounded-lg text-[11px] font-semibold text-slate-700 outline-none focus:ring-2 focus:ring-indigo-500"
                              />

                            </div>


                            {dateEditError && (

                              <p className="text-[10px] font-semibold text-red-600 leading-relaxed">
                                {dateEditError}
                              </p>

                            )}


                            <div className="flex flex-wrap gap-1.5">

                              <button
                                type="button"
                                onClick={() =>
                                  handleSaveDateEdit(
                                    row
                                  )
                                }
                                className="px-2.5 py-1.5 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-[10px] font-black transition"
                              >
                                Kaydet
                              </button>

                              <button
                                type="button"
                                onClick={
                                  handleCancelDateEdit
                                }
                                className="px-2.5 py-1.5 bg-white border border-slate-200 text-slate-500 rounded-lg text-[10px] font-black hover:bg-slate-50 transition"
                              >
                                Vazgeç
                              </button>

                            </div>

                          </div>

                       ) : (

  <div className="space-y-2">

  {row.startDate &&
  row.endDate ? (

    <div>

      <p className="text-xs font-black text-slate-800 whitespace-nowrap">
        {formatDate(
          row.startDate
        )}
      </p>

      <p className="text-[9px] text-slate-400 mt-0.5 whitespace-nowrap">
        → {formatDate(
          row.endDate
        )}
      </p>

    </div>

  ) : (

    <span className="inline-flex px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-[9px] font-black text-amber-700">
      Tarih atanmadı
    </span>

  )}


  <div className="flex flex-wrap items-center gap-1.5">

    <button
      type="button"
      onClick={() =>
        handleStartDateEdit(
          row
        )
      }
      className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[9px] font-black text-slate-500 hover:border-indigo-200 hover:text-indigo-600 transition"
    >
      Düzenle
    </button>


    {row.dateSource ===
      "manual_override" && (

      <span className="px-2 py-1 rounded-lg bg-emerald-50 text-[8px] font-black text-emerald-700">
        Manuel
      </span>

    )}

  </div>

</div>

                        )}

                      </td>


                      <td className="px-4 py-3 align-top min-w-[210px]">

  <p
    className={`text-[11px] font-black leading-relaxed ${
      row.targetType
        ? "text-violet-700"
        : "text-slate-400"
    }`}
  >
    {getTargetDisplayLabel(row)}
  </p>

</td>


<td className="px-4 py-3 align-top min-w-[190px]">

  {getActivityTypeDisplayLabel(
    row
  ) ? (

    <p className="text-xs font-bold text-slate-700 leading-relaxed">
      {getActivityTypeDisplayLabel(
        row
      )}
    </p>

  ) : (

    <span className="text-xs text-slate-400 font-semibold">
      —
    </span>

  )}


  {audienceLabels[
  row.audience
] && (

  <p className="text-[10px] text-slate-400 mt-1 font-semibold">
    Hedef Kitle:{" "}
    {
      audienceLabels[
        row.audience
      ]
    }
  </p>

)}

</td>

<td className="px-4 py-3 align-top min-w-[250px]">

  {row?.coverage?.mode ===
  "not_applicable" ? (

    <span className="text-xs font-semibold text-slate-400">
      —
    </span>

  ) : (

    <div>

     <div className="flex items-start justify-between gap-2">

  <div className="min-w-0">

    {row?.coverage?.mode ===
      "selected_classes" &&
    row?.coverage?.resolved ===
      false ? (

      <span className="inline-flex px-2 py-1 rounded-lg bg-amber-50 border border-amber-200 text-[9px] font-black text-amber-700">
        Sınıf seçilmedi
      </span>

    ) : (

      <span className="inline-flex px-2 py-1 rounded-lg bg-slate-50 border border-slate-200 text-[9px] font-black text-slate-700">
        {row?.coverage?.mode ===
        "all_classes"
          ? getApplicableClassOptionsForRow(
              row,
              planClassOptions
            ).length > 0
            ? `Tüm Sınıflar • ${
                getApplicableClassOptionsForRow(
                  row,
                  planClassOptions
                ).length
              }`
            : "Tüm Sınıflar"
          : `${
              Array.isArray(
                row?.coverage?.classIds
              )
                ? row.coverage.classIds.length
                : 0
            } sınıf seçili`}
      </span>

    )}


    {row?.coverage?.mode ===
      "selected_classes" &&
      row?.coverage?.resolved !==
        false &&
      row?.coverage?.displayLabel && (

      <p
        className="text-[9px] text-slate-400 mt-1.5 leading-relaxed max-w-[180px]"
        title={
          row.coverage.displayLabel
        }
      >
        {row.coverage.displayLabel}
      </p>

    )}

  </div>


  <button
    type="button"
    onClick={() =>
      setEditingCoverageRowId(
        previous =>
          previous === row.id
            ? null
            : row.id
      )
    }
    className="px-2 py-1 bg-white border border-slate-200 rounded-lg text-[9px] font-black text-slate-500 hover:border-indigo-200 hover:text-indigo-600 transition shrink-0"
  >
    {editingCoverageRowId ===
    row.id
      ? "Kapat"
      : "Düzenle"}
  </button>

</div>


      {editingCoverageRowId ===
        row.id && (

        <div className="mt-3 bg-slate-50 border border-slate-200 rounded-xl p-3">

          {planClassOptions.length ===
          0 ? (

            <div>

              <p className="text-[10px] font-black text-amber-700">
                Sınıf/şube yapısı tanımlanmadı
              </p>

              <p className="text-[10px] text-amber-600 mt-1 leading-relaxed">
                Önce yukarıdaki Sınıf / Şube Yapısı alanından
                okulunuzun şubelerini tanımlayın.
              </p>

            </div>

          ) : (

            <>

              <div className="flex items-center justify-between gap-2 mb-3">

                <p className="text-[10px] font-black uppercase tracking-wider text-slate-400">
                  Çalışmaya Dahil Sınıflar
                </p>

                <button
                  type="button"
                  onClick={() =>
                    handleSetRowAllClasses(
                      row.id
                    )
                  }
                  className="text-[10px] font-black text-emerald-700 hover:text-emerald-800"
                >
                  Tümünü Seç
                </button>

              </div>


              <div className="flex flex-wrap gap-2">

                {getApplicableClassOptionsForRow(
  row,
  planClassOptions
).map(
  classItem => {

                    const selected =
                      getRowSelectedClassIds(
                        row
                      ).includes(
                        classItem.id
                      );


                    return (

                      <button
                        type="button"
                        key={classItem.id}
                        onClick={() =>
                          handleToggleRowClass(
                            row.id,
                            classItem
                          )
                        }
                        className={`px-2.5 py-1.5 rounded-lg border text-[10px] font-black transition ${
                          selected
                            ? "bg-indigo-600 border-indigo-600 text-white"
                            : "bg-white border-slate-200 text-slate-500 hover:border-indigo-300"
                        }`}
                      >
                        {classItem.label}
                      </button>

                    );
                  }
                )}

              </div>

            </>

          )}

        </div>

      )}

    </div>

  )}

</td>

                    </tr>

                  );
                }
              )}

          </tbody>

        </table>

      </div>

    </div>



    <div className="bg-slate-50 border border-slate-200 rounded-2xl p-4">

      <div className="flex items-start gap-3">

        <CheckCircle2 className="h-5 w-5 text-emerald-600 shrink-0 mt-0.5" />

        <div>
<p className="font-black text-slate-800">
  Yıllık planınız hazır
</p>

<p className="text-xs text-slate-500 mt-1 leading-relaxed">
  Faaliyetleri, tarihleri ve sınıf kapsamlarını kontrol edin.
  Her şey uygunsa planınızı Excel'e aktarabilirsiniz.
</p>

{(
  autoTargetResolutionReport
    ?.slotReport
    ?.recommendedUnresolved
    ?.length || 0
) > 0 && (

  <p className="text-[10px] text-amber-600 mt-2 leading-relaxed">
    Bazı önerilen çalışmalar için doğrulanmış resmî faaliyet
    bulunamadığından bu çalışmalar plana eklenmedi.
    Bu durum planın oluşturulmasını engellemez.
  </p>

)}
        </div>

      </div>

    </div>

  </div>

)}

    </div>
  );
};




export default PlanningCenter;